import type { ZodiosOptions } from "@zodios/core";

import { makeApi, Zodios } from "@zodios/core";
import { z } from "zod";

import { zodPathParam } from "@shared/zodios";

const DOB_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const PortalUserInfo = z.object({
	email: z.union([z.string(), z.null()]).optional(),
	email_verified: z.union([z.boolean(), z.null()]).optional(),
	intercom_user_jwt: z.string(),
	name: z.union([z.string(), z.null()]).optional(),
	permissions: z.array(z.string()),
	picture: z.union([z.string(), z.null()]).optional(),
	scopes: z.array(z.string()),
	sub: z.string(),
});
const WireBankDetails = z.object({
	account_number: z.string().max(27),
	routing_number: z.string().max(12),
});
const ScanDetails = z.object({ account_number: z.string().max(8), sort_code: z.string().max(6) });
const AccountDetailsIdentifier = z.union([
	z.object({ iban: z.string() }),
	z.object({ wire: WireBankDetails }),
	z.object({ scan: ScanDetails }),
]);
const Address = z.object({
	country_code: z.string().min(2).max(2),
	line_1: z.string().max(50),
	line_2: z.union([z.string(), z.null()]).optional(),
	line_3: z.union([z.string(), z.null()]).optional(),
	line_4: z.union([z.string(), z.null()]).optional(),
});
const BicCode = z.string();
const IntermediaryBank = z.object({
	account_number: z.union([z.string(), z.null()]).optional(),
	code: BicCode,
});
const BeneficiaryBank = z
	.object({
		branch: z.union([z.string(), z.null()]),
		code: z.union([z.string(), z.null()]),
		intermediary_bank: z.union([z.null(), IntermediaryBank]),
	})
	.partial();
const uae_id = z.object({ number: z.string().max(50) });
const passport = z.object({ country_of_issue: z.string(), number: z.string().max(50) });
const Identification = z.union([
	uae_id.merge(z.object({ type: z.literal("uae_id") })),
	passport.merge(z.object({ type: z.literal("passport") })),
]);
const CounterpartType = z.enum(["individual", "business"]);
const PaymentDetails = z.object({
	account_holder_name: z.string().max(75),
	account_identifier: AccountDetailsIdentifier,
	address: Address,
	bank: z.union([z.null(), BeneficiaryBank]).optional(),
	date_of_birth: z.union([z.string(), z.null()]).optional(),
	email: z.union([z.string(), z.null()]).optional(),
	identification: z.union([z.null(), Identification]).optional(),
	nationality: z.union([z.string(), z.null()]).optional(),
	phone: z.union([z.string(), z.null()]).optional(),
	type: CounterpartType,
});
const PurposeCode = z.enum([
	"charity",
	"commercial_investment",
	"corporate_card",
	"credit_card",
	"dividend",
	"family",
	"financial_services",
	"goods_sold",
	"goods_bought",
	"government",
	"insurance",
	"intergroup_transfer",
	"intra_group_dividends",
	"information_technology",
	"leasing",
	"loan_charges",
	"merchant_settlement",
	"mobile_wallet",
	"non_resident_transfer_between_accounts",
	"none",
	"pension",
	"personal_expenses",
	"prepaid_cards",
	"professional",
	"rental",
	"resident_transfer_between_accounts",
	"salaries",
	"tax",
	"telecommunications",
	"travel",
	"utility_bill",
]);
const CreateCustomerPaymentRequest = z.object({
	amount: z.number().int().gte(0),
	beneficiary: PaymentDetails,
	external_id: z.union([z.string(), z.null()]).optional(),
	external_reference: z.string().max(100),
	purpose: PurposeCode,
});
const AuthorizePaymentResponse = z.object({ redirect_url: z.string() });
const cursor = z.union([z.string(), z.null()]).optional();
const PageMetadata = z.object({
	cursor: z.union([z.string(), z.null()]).optional(),
	page_size: z.number().int().gte(0),
	total_count: z.number().int().gte(0),
	total_pages: z.number().int().gte(0),
});
const ResourceAction = z.enum(["create", "update", "delete"]);
const Currency = z.enum(["AED", "USD", "JOD", "EGP", "SAR", "GBP", "EUR", "HKD", "CNH", "SGD"]);
const ResourcePayloadResponse = z.union([
	z.object({
		account_id: z.string(),
		account_name: z.string(),
		currency: Currency,
		request: CreateCustomerPaymentRequest,
		type: z.literal("create_payment"),
	}),
	z.object({
		batch_id: z.string(),
		file_name: z.string(),
		payment_count: z.number().int().gte(0),
		type: z.literal("create_batch_payment"),
	}),
]);
const ResourceType = z.enum(["payment", "batch_payment"]);
const ApprovalRequestState = z.enum(["pending", "approved", "rejected"]);
const ApprovalRequestResponse = z.object({
	action: ResourceAction,
	checker_name: z.union([z.string(), z.null()]).optional(),
	checker_user_id: z.union([z.string(), z.null()]).optional(),
	comment: z.union([z.string(), z.null()]).optional(),
	created_at: z.string().regex(DOB_REGEX),
	id: z.string(),
	maker_name: z.union([z.string(), z.null()]).optional(),
	maker_user_id: z.string(),
	resource_id: z.union([z.string(), z.null()]).optional(),
	resource_payload: ResourcePayloadResponse,
	resource_type: ResourceType,
	state: ApprovalRequestState,
	updated_at: z.string().datetime(),
});
const ListApprovalRequestsResponse = z.object({
	pages: PageMetadata,
	results: z.array(ApprovalRequestResponse),
});
const BatchType = z.literal("LocalPayment");
const UploadBatchFileRequest = z.object({
	batch_type: BatchType,
	file: z.string(),
	file_name: z.string(),
});
const ResourcePayloadRequest = z.union([
	z.object({
		account_id: z.string(),
		request: CreateCustomerPaymentRequest,
		type: z.literal("create_payment"),
	}),
	z.object({
		request: UploadBatchFileRequest,
		type: z.literal("create_batch_payment"),
	}),
]);
const InitiateApprovalRequestRequest = z.object({ resource: ResourcePayloadRequest });
const InitiateApprovalRequestResponse = z.object({ approval_request_id: z.string() });
const ApproveApprovalRequestResponse = z.union([
	z.object({ redirect_url: z.string(), type: z.literal("sca_required") }),
	z.object({ resource_id: z.string(), type: z.literal("approved") }),
]);
const RejectApprovalRequestRequest = z.object({ comment: z.string() });
const EnableBankIntegrationRequest = z.object({ bank: z.string() });
const AccountIdentifier = z.union([
	z.object({ iban: z.string() }),
	z.object({ wire: WireBankDetails }),
	z.object({ scan: ScanDetails }),
]);
const BeneficiaryResponse = z.object({
	account_identifier: z.union([z.null(), AccountIdentifier]).optional(),
	account_name: z.string().max(140),
	account_type: CounterpartType,
	address: z.union([z.null(), Address]).optional(),
	bank: z.union([z.null(), BeneficiaryBank]).optional(),
	currency: Currency,
	date_of_birth: z.union([z.string(), z.null()]).optional(),
	display_name: z.string().max(64),
	email: z.union([z.string(), z.null()]).optional(),
	id: z.string(),
	national_id: z.union([z.string(), z.null()]).optional(),
	nationality: z.union([z.string(), z.null()]).optional(),
	organisation_id: z.string(),
	phone: z.union([z.string(), z.null()]).optional(),
});
const ListBeneficiariesPagedResponse = z.object({
	pages: PageMetadata,
	results: z.array(BeneficiaryResponse),
});
const SaveBeneficiaryRequest = z.object({
	account_identifier: z.union([z.null(), AccountIdentifier]).optional(),
	account_name: z.string().max(140),
	account_type: CounterpartType,
	address: z.union([z.null(), Address]).optional(),
	bank: z.union([z.null(), BeneficiaryBank]).optional(),
	currency: Currency,
	date_of_birth: z.union([z.string(), z.null()]).optional(),
	display_name: z.string().max(64),
	email: z.union([z.string(), z.null()]).optional(),
	national_id: z.union([z.null(), Identification]).optional(),
	nationality: z.union([z.string(), z.null()]).optional(),
	phone: z.union([z.string(), z.null()]).optional(),
});
const page_size = z.union([z.number(), z.null()]).optional();
const CashManagementTransactionType = z.enum([
	"buy_order",
	"sell_order",
	"outbound_transfer",
	"inbound_transfer",
	"credit",
	"debit",
]);
const transaction_type = z.union([z.null(), CashManagementTransactionType]).optional();
const CashManagementTransactionState = z.enum(["pending", "completed", "failed"]);
const state = z.union([z.null(), CashManagementTransactionState]).optional();
const CashManagementTransaction = z.object({
	amount: z.number().int().gte(0),
	asset_isin: z.union([z.string(), z.null()]).optional(),
	asset_name: z.union([z.string(), z.null()]).optional(),
	cash_management_account_id: z.string(),
	created_timestamp: z.string().regex(DOB_REGEX),
	currency: z.union([z.null(), Currency]).optional(),
	external_reference: z.union([z.string(), z.null()]).optional(),
	failure_reason: z.union([z.string(), z.null()]).optional(),
	fund_id: z.union([z.string(), z.null()]).optional(),
	id: z.string(),
	last_updated_timestamp: z.string().regex(DOB_REGEX),
	organisation_id: z.string(),
	price: z.union([z.string(), z.null()]).optional(),
	quantity: z.union([z.string(), z.null()]).optional(),
	relates_to: z.union([z.string(), z.null()]).optional(),
	state: CashManagementTransactionState,
	transaction_type: CashManagementTransactionType,
});
const ListCashManagementTransactionsResponse = z.object({
	pages: PageMetadata,
	results: z.array(CashManagementTransaction),
});
const RetryCashManagementTransferResponse = z.object({ transfer_id: z.string() });
const CreateClientResponse = z.object({ client_id: z.string(), client_secret: z.string() });
const ErrorCode = z.enum([
	"missing_document",
	"missing_currency_data",
	"missing_compliance_checks",
	"no_shareholders",
	"insufficient_majority_owership",
	"shareholder_not_onboarded",
	"business_registration_date_missing",
	"invalid_business_registration_date",
	"business_registration_number_missing",
	"business_trade_license_expiry_missing",
	"expired_trade_license",
	"expired_identification_document",
	"individual_missing_proof_of_address_document",
	"business_missing_proof_of_address_document",
	"invalid_documents",
	"invalid_customer_name",
	"invalid_shareholder_name",
	"invalid_customer_address",
	"unsupported_country",
	"invalid_tax_number",
	"invalid_identification",
	"invalid_license_issuer",
	"missing_onboarding_data",
]);
const OnboardingError = z.object({ code: ErrorCode, msg: z.string() });
const OnboardingStatus = z.union([
	z.literal("pending"),
	z.literal("completed"),
	z.object({ failed: OnboardingError }),
]);
const AedOnboarding = z.object({
	customer_id: z.string(),
	onboarded: z.boolean(),
	status: OnboardingStatus,
});
const Dlq = z.object({ name: z.string(), pending_messages: z.number().int().gte(0) });
const GetDlqsResponse = z.object({ dlqs: z.array(Dlq) });
const DropDlqMessagesRequest = z.object({ message_ids: z.array(z.string()) });
const DropDlqMessageOutcome = z.union([
	z.object({ outcome: z.literal("not_found") }),
	z.object({ outcome: z.literal("success") }),
	z.object({ message: z.string(), outcome: z.literal("error") }),
]);
const DropDlqMessagesResponse = z.object({ outcomes: z.record(DropDlqMessageOutcome) });
const DlqMessage = z.object({
	body: z.union([z.string(), z.null()]).optional(),
	dlq_timestamp: z.union([z.string(), z.null()]).optional(),
	first_consume_timestamp: z.union([z.string(), z.null()]).optional(),
	id: z.string(),
	routing_key: z.union([z.string(), z.null()]).optional(),
	span_id: z.union([z.string(), z.null()]).optional(),
	trace_id: z.union([z.string(), z.null()]).optional(),
});
const GetDlqMessagesResponse = z.object({ messages: z.array(DlqMessage) });
const RetryDlqMessagesRequest = z.object({ message_ids: z.array(z.string()) });
const RetryDlqMessageOutcome = z.union([
	z.object({ outcome: z.literal("not_found") }),
	z.object({ outcome: z.literal("success") }),
	z.object({ message: z.string(), outcome: z.literal("error") }),
]);
const RetryDlqMessagesResponse = z.object({ outcomes: z.record(RetryDlqMessageOutcome) });
const FeatureFlagName = z.enum([
	"enable_forbid_aed_for_uae_individual",
	"disable_aed_account_creation",
]);
const UpdateFeatureFlagRequest = z.object({
	enable: z.boolean(),
	feature_flag: FeatureFlagName,
	reason: z.union([z.string(), z.null()]).optional(),
});
const NexusVolumeResponse = z.object({
	currency: Currency,
	transaction_count: z.number().int().gte(0),
	volume: z.number().int().gte(0),
});
const OperationType = z.enum([
	"cross_bank_forex",
	"forex",
	"safeguarded_payment",
	"payment",
	"inbound",
	"returned",
	"transfer",
]);
const Status = z.enum(["created", "pending", "completed", "failed"]);
const OperationSummary = z.object({
	id: z.string(),
	operation_type: OperationType,
	status: Status,
	timestamp: z.string().regex(DOB_REGEX),
});
const SafeguardedForex = z.object({ id: z.string() });
const CrossBankForexState = z.union([
	z.object({ status: z.literal("created") }),
	z.object({ status: z.literal("pending") }),
	z.object({
		reason: z.union([z.string(), z.null()]).optional(),
		status: z.literal("waiting"),
	}),
	z.object({ status: z.literal("completed") }),
	z.object({ status: z.literal("failed") }),
]);
const AmountInMinor = z.object({ amount: z.number().int().gte(0), currency: Currency });
const Party = z.object({
	account_id: z.string(),
	amount: AmountInMinor,
	bank: z.string(),
	currency: Currency,
});
const Rate = z.object({
	customer_rate: z.number(),
	fuse_rate: z.number(),
	profit: AmountInMinor,
	profit_in_usd: z.number().int().gte(0),
	to_amount_without_spread: AmountInMinor,
	to_currency_usd_rate: z.number(),
});
const CrossBankForex = CrossBankForexState.and(
	z.object({
		from: Party,
		id: z.string(),
		org_id: z.string(),
		rate: Rate,
		to: Party,
	}),
);
const Forex = z.object({ id: z.string() });
const Passport = z.object({ country_of_issue: z.string(), number: z.string() });
const IdNumber = z.union([z.object({ uae_id: z.string() }), z.object({ passport: Passport })]);
const LocalCounterpart = z.object({
	account_identifier: AccountIdentifier,
	address: Address,
	bank_branch: z.union([z.string(), z.null()]).optional(),
	bank_code: z.string().min(8),
	counterpart_type: CounterpartType,
	date_of_birth: z.union([z.string(), z.null()]).optional(),
	email: z.union([z.string(), z.null()]).optional(),
	id_number: z.union([z.null(), IdNumber]).optional(),
	intermediary_bank: z.union([z.null(), IntermediaryBank]).optional(),
	name: z.string().max(140),
	nationality: z.union([z.string(), z.null()]).optional(),
	phone: z.union([z.string(), z.null()]).optional(),
});
const SafeguardedPaymentState = z.enum([
	"payment_created",
	"payment_failed",
	"payment_executed",
	"failed",
	"executed",
]);
const SafeguardedPayment = z.object({
	account_id: z.string(),
	beneficiary: LocalCounterpart,
	id: z.string(),
	instructed_amount: AmountInMinor,
	org_id: z.string(),
	purpose: PurposeCode,
	reference: z.string(),
	safeguarded_amount: AmountInMinor,
	state: SafeguardedPaymentState,
	timestamp: z.string().regex(DOB_REGEX),
	transaction_id: z.string(),
});
const Payment = z.object({ id: z.string() });
const Inbound = z.object({ id: z.string() });
const Returned = z.object({ id: z.string() });
const Transfer = z.object({ id: z.string() });
const Operation = z.union([
	SafeguardedForex.merge(z.object({ operation_type: z.literal("safeguarded_forex") })),
	CrossBankForex.and(z.object({ operation_type: z.literal("cross_bank_forex") })),
	Forex.merge(z.object({ operation_type: z.literal("forex") })),
	SafeguardedPayment.merge(z.object({ operation_type: z.literal("safeguarded_payment") })),
	Payment.and(z.object({ operation_type: z.literal("payment") })),
	Inbound.and(z.object({ operation_type: z.literal("inbound") })),
	Returned.and(z.object({ operation_type: z.literal("returned") })),
	Transfer.and(z.object({ operation_type: z.literal("transfer") })),
]);
const update_OperationType = z.union([
	z.object({ operation_type: z.literal("safeguarded_payment") }),
	z.object({
		command_id: z.string(),
		operation_type: z.literal("cross_bank_forex"),
	}),
	z.object({
		command_id: z.string(),
		operation_type: z.literal("cross_bank_forex_fail"),
	}),
]);
const FeeSourceAccount = z.enum(["transaction_account", "primary_account"]);
const OnboardingMode = z.enum(["reliance", "direct"]);
const WebhookType = z.enum([
	"account_opened",
	"account_updated",
	"account_frozen",
	"account_unfrozen",
	"account_closed",
	"outbound_local_payment_created",
	"outbound_local_payment_submitted",
	"outbound_local_payment_executed",
	"outbound_local_payment_failed",
	"outbound_local_payment_returned",
	"inbound_local_payment_settled",
	"fee_debited",
	"internal_transfer_created",
	"inbound_internal_transfer_settled",
	"internal_transfer_settled",
	"internal_transfer_failed",
	"outbound_exchange_transfer_created",
	"inbound_exchange_transfer_settled",
	"outbound_exchange_transfer_submitted",
	"outbound_exchange_transfer_executed",
	"outbound_exchange_transfer_failed",
	"onboarding_completed",
	"onboarding_failed",
	"customer_approved",
	"customer_rejected",
]);
const WebhookEntry = z.object({
	display_name: z.string(),
	enabled: z.boolean(),
	type: WebhookType,
});
const WebhookGroup = z.object({
	description: z.string(),
	group_name: z.string(),
	subscriptions: z.array(WebhookEntry),
});
const OrganisationView = z.object({
	api_enabled: z.boolean(),
	archived_at: z.union([z.string(), z.null()]).optional(),
	client_id: z.union([z.string(), z.null()]).optional(),
	created_at: z.string().regex(DOB_REGEX),
	fedwire_enabled: z.boolean(),
	fee_source_account: FeeSourceAccount,
	id: z.string(),
	maker_checker_enabled: z.boolean(),
	name: z.string(),
	onboarding_mode: OnboardingMode,
	stripe_customer_id: z.union([z.string(), z.null()]).optional(),
	webhook_groups: z.array(WebhookGroup),
});
const FxPrice = z.union([
	z.object({ rate: z.number(), type: z.literal("fixed") }),
	z.object({ bps: z.number(), type: z.literal("spread") }),
]);
const ForexEntry = z.object({ from_currency: Currency, price: FxPrice, to_currency: Currency });
const ForexEntries = z.object({ forex_pricing: z.array(ForexEntry) });
const FixedFee = z.object({ amount: z.number().gte(0) });
const IbanFeeTypes = FixedFee.and(z.object({ type: z.literal("fixed") }));
const IbanSingleTier = z.object({ fee_type: IbanFeeTypes });
const IbanFirstTier = z.object({ fee_type: IbanFeeTypes, to: z.number() });
const IbanLastTier = z.object({ fee_type: IbanFeeTypes, from: z.number() });
const IbanTier = z.object({ fee_type: IbanFeeTypes, from: z.number(), to: z.number() });
const IbanMultiTier = z.object({
	first_tier: IbanFirstTier,
	last_tier: IbanLastTier,
	middle_tiers: IbanTier,
});
const IbanTierType = z.union([
	IbanSingleTier.and(z.object({ tier_type: z.literal("single") })),
	IbanMultiTier.and(z.object({ tier_type: z.literal("multi") })),
]);
const IbanWriteFee = IbanTierType;
const MonthlyMinimum = z.object({ amount: z.number().gte(1), start: z.string() });
const BasisPointsWithBase = z.object({ base: z.number().gte(0), percentage: z.number().gte(0) });
const BasisPoints = z.object({
	cap: z.union([z.number(), z.null()]).optional(),
	percentage: z.number().gte(0),
});
const FeeType = z.union([
	BasisPointsWithBase.and(z.object({ type: z.literal("basis_points_with_base") })),
	BasisPoints.and(z.object({ type: z.literal("basis_points") })),
	FixedFee.and(z.object({ type: z.literal("fixed") })),
]);
const LocalDecimalFirstTier = z.object({ fee_type: FeeType, to: z.number() });
const LocalDecimalLastTier = z.object({ fee_type: FeeType, from: z.number() });
const LocalDecimalTier = z.object({ fee_type: FeeType, from: z.number(), to: z.number() });
const LocalWriteFeeDecimal = z.object({
	first_tier: LocalDecimalFirstTier,
	last_tier: LocalDecimalLastTier,
	middle_tiers: z.array(LocalDecimalTier),
	minimum_fee: z.union([z.number(), z.null()]).optional(),
	vat: z.union([z.number(), z.null()]).optional(),
});
const LocalIntegerFirstTier = z.object({ fee_type: FeeType, to: z.number() });
const LocalIntegerLastTier = z.object({ fee_type: FeeType, from: z.number() });
const LocalIntegerTier = z.object({ fee_type: FeeType, from: z.number(), to: z.number() });
const LocalWriteFeeInteger = z.object({
	first_tier: LocalIntegerFirstTier,
	last_tier: LocalIntegerLastTier,
	middle_tiers: z.array(LocalIntegerTier),
	minimum_fee: z.union([z.number(), z.null()]).optional(),
	vat: z.union([z.number(), z.null()]).optional(),
});
const LocalWriteFee = z.union([
	LocalWriteFeeDecimal.and(z.object({ fee_from: z.literal("Amount") })),
	LocalWriteFeeDecimal.and(z.object({ fee_from: z.literal("Volume") })),
	LocalWriteFeeInteger.and(z.object({ fee_from: z.literal("TransactionCount") })),
]);
const WriteFeeSpecCondition = z.union([
	z.object({
		InAndOut: z.object({ inbound: LocalWriteFee, outbound: LocalWriteFee }),
	}),
	z.object({ ByCurrency: z.object({ fee: LocalWriteFee }) }),
]);
const WriteFeeSpec = z.object({
	apr_tiers: z.union([z.object({}).partial(), z.null()]).optional(),
	cash_management_transfers: z.union([z.object({}).partial(), z.null()]).optional(),
	iban: IbanWriteFee,
	monthly_minimum: z.union([z.array(MonthlyMinimum), z.null()]).optional(),
	override_fees: z.union([z.object({}).partial(), z.null()]).optional(),
	transaction: z.record(WriteFeeSpecCondition),
});
const StoreFeeRequest = ForexEntries.and(
	z.object({
		fee_spec: WriteFeeSpec,
		nexus_subscriber: z.union([z.boolean(), z.null()]).optional(),
	}),
);
const CreateOrganisationRequest = z.object({
	api_enabled: z.union([z.boolean(), z.null()]).optional(),
	fedwire_enabled: z.union([z.boolean(), z.null()]).optional(),
	fee: z.union([z.null(), StoreFeeRequest]).optional(),
	fee_source_account: z.union([z.null(), FeeSourceAccount]).optional(),
	maker_checker_enabled: z.union([z.boolean(), z.null()]).optional(),
	onboarding_mode: z.union([z.null(), OnboardingMode]).optional(),
	org_name: z.string(),
});
const CreateOrganisationResponse = z.object({ org_id: z.string() });
const UpdateOrganisationRequest = z
	.object({
		organisation_name: z.union([z.string(), z.null()]),
		stripe_customer_id: z.union([z.string(), z.null()]),
	})
	.partial();
const IbanReadFee = z.object({ tiers: z.array(IbanTier) });
const InternationalFeeTypes = z.union([
	BasisPoints.and(z.object({ type: z.literal("basis_points") })),
	FixedFee.and(z.object({ type: z.literal("fixed") })),
]);
const InternationalTier = z.object({
	fee_type: InternationalFeeTypes,
	from: z.number(),
	to: z.number(),
});
const InternationalReadFee = z.object({
	minimum_fee: z.union([z.number(), z.null()]).optional(),
	tiers: z.array(InternationalTier),
});
const LocalReadFeeDecimal = z.object({
	minimum_fee: z.union([z.number(), z.null()]).optional(),
	tiers: z.array(LocalDecimalTier),
	vat: z.union([z.number(), z.null()]).optional(),
});
const LocalReadFeeInteger = z.object({
	minimum_fee: z.union([z.number(), z.null()]).optional(),
	tiers: z.array(LocalDecimalTier),
	vat: z.union([z.number(), z.null()]).optional(),
});
const LocalReadFee = z.union([
	LocalReadFeeDecimal.and(z.object({ fee_from: z.literal("Amount") })),
	LocalReadFeeDecimal.and(z.object({ fee_from: z.literal("Volume") })),
	LocalReadFeeInteger.and(z.object({ fee_from: z.literal("TransactionCount") })),
]);
const ReadOrgFeeResponse = z.object({
	iban: z.union([z.null(), IbanReadFee]).optional(),
	international: z.record(InternationalReadFee),
	local: LocalReadFee,
	monthly_minimum: z.union([z.array(MonthlyMinimum), z.null()]).optional(),
	nexus_subscriber: z.boolean().optional(),
});
const OrganisationFees = z.object({ fees: ReadOrgFeeResponse, organisation_id: z.string() });
const AllOrganisationsFeesResponse = z.object({ organisation_fees: z.array(OrganisationFees) });
const GetOrganisationConfigResponse = z
	.object({ webhook: z.union([z.string(), z.null()]) })
	.partial();
const WebhookConfigRequest = z.object({ webhook_url: z.string().url() });
const CreateAccountType = z.enum(["payment", "virtual"]);
const EnabledForCurrency = z.object({ account_types: z.array(CreateAccountType) });
const EnabledCurrenciesResponse = z.record(EnabledForCurrency);
const PublicKeyResponse = z.object({
	created_at: z.string().regex(DOB_REGEX),
	created_by: z.string(),
	keyid: z.string(),
	label: z.union([z.string(), z.null()]).optional(),
});
const RegisterPublicKeyRequest = z.object({
	label: z.union([z.string(), z.null()]).optional(),
	pem: z.string(),
});
const User = z.object({ email: z.string(), name: z.string(), user_id: z.string() });
const InvitedUser = z.object({
	email: z.string(),
	expires_at: z.string().regex(DOB_REGEX),
	invite_id: z.string(),
	invited_by: z.string(),
});
const ListUsersForOrganisationResponse = z.object({
	active_users: z.array(User),
	invited_users: z.array(InvitedUser),
});
const InviteUserRequest = z.object({
	add_to_role_name: z.string(),
	email: z.string().min(1),
	inviter: z.string().min(1).max(300),
});
const RegisterInvitedUserRequest = z.object({ org_id: z.string(), user_id: z.string() });
const AssignRoleToUserRequest = z.object({ role_name: z.string() });
const Role = z.object({ description: z.string(), id: z.string(), name: z.string() });
const UserRolesRequest = z.object({ role_ids: z.array(z.string()) });
const UpdateWebhooksRequest = z.object({ webhook_subscriptions: z.array(WebhookType) }).partial();
const AccountType = z.enum(["primary", "payment", "virtual"]);
const PaymentScheme = z.enum([
	"uae_fts",
	"uae_ipp",
	"swift",
	"fedwire",
	"ach",
	"sepa",
	"sepa_instant",
	"faster_payments",
]);
const AddressableDetails = z.object({
	account_details: AccountDetailsIdentifier,
	payment_schemes: z.array(PaymentScheme),
});
const AccountStatus = z.enum(["open", "frozen", "closed"]);
const Account = z.object({
	account_name: z.string().max(140),
	account_owner: z.string().max(140),
	account_type: AccountType,
	addressable_details: z.array(AddressableDetails),
	available_balance: z.number().int().gte(0),
	currency: Currency,
	current_balance: z.number().int().gte(0),
	customer_id: z.union([z.string(), z.null()]).optional(),
	default_details: z.union([z.null(), AccountDetailsIdentifier]).optional(),
	external_id: z.union([z.string(), z.null()]).optional(),
	id: z.string(),
	status: AccountStatus,
});
const ArchiveOrganisationResponse = z.object({ archived_at: z.string().datetime() });
const CreateCashManagementAccountRequest = z.object({
	account_name: z.string().max(140),
	account_type: z.string(),
	command_id: z.string(),
	currency: Currency,
	customer_id: z.string(),
	internal_account_id: z.string(),
});
const CreateCashManagementAccountResponse = z.object({ account_id: z.string() });
const AprTierResponse = z.object({
	account_rate_bps: z.number().int(),
	correspondent_rate_bps: z.number().int(),
	is_default: z.boolean(),
	name: z.string(),
});
const ApprovalStatus = z.enum(["auto_approved", "pending", "approved", "rejected"]);
const CustomerTypeFilter = z.enum(["individual", "business"]);
const CustomerRelationshipType = z.union([
	z.literal("direct"),
	z.object({
		indirect: z.object({ parent_customer_id: z.string() }),
	}),
]);
const CustomerSummary = z.object({
	approval_status: ApprovalStatus,
	created_at: z.string().regex(DOB_REGEX),
	customer_type: CustomerTypeFilter,
	display_name: z.string(),
	id: z.string(),
	reference: z.string().max(36),
	relationship_type: CustomerRelationshipType,
});
const Cursor_String = z.object({
	next: z.union([z.string(), z.null()]).optional(),
	size: z.number().int().gte(0),
});
const ListCustomersResponse = z.object({
	customers: z.array(CustomerSummary),
	pages: Cursor_String,
});
const IdDocumentType = z.enum(["passport", "driver_licence", "national_id"]);
const IndividualCurrencyExtra = z.object({
	country_of_birth: z.string().min(2).max(2),
	country_of_issuance: z.string().min(2).max(2),
	currency: z.literal("usd"),
	id_expiry_date: z.union([z.string(), z.null()]),
	id_issuing_date: z.union([z.string(), z.null()]).optional(),
	id_number: z.string().min(1).max(100),
	id_type_code: z.union([z.null(), IdDocumentType]),
	ip_address: z.string().min(2).max(45).optional(),
	occupation_code: z.string().min(6).max(6),
	us_tax_number: z.union([z.string(), z.null()]).optional(),
});
const Gender = z.enum(["male", "female", "undefined"]);
const IndividualIdentification = z.union([
	z.object({
		country_of_issuance: z.string().min(2).max(2),
		expiry_date: z.string(),
		number: z.string().min(1).max(100),
		type: z.literal("passport"),
	}),
	z.object({
		country_of_issuance: z.string().min(2).max(2),
		expiry_date: z.string(),
		number: z.string().min(1).max(100),
		type: z.literal("national_id"),
	}),
]);
const AedLicenseIssuer = z.enum(["mainland", "freezone", "international"]);
const BusinessCurrencyExtra = z.union([
	z.object({
		currency: z.literal("usd"),
		industry_code: z.string().min(6).max(6),
		ip_address: z.string().min(2).max(45).optional(),
		tax_number: z.string().min(1).max(100),
	}),
	z.object({
		currency: z.literal("aed"),
		license_issuer: AedLicenseIssuer,
		trade_license_expiry: z.union([z.string(), z.null()]).optional(),
	}),
]);
const RegulatoryLicenseType = z.enum([
	"banking",
	"card_acquiring",
	"card_issuer",
	"crypto_asset",
	"electronic_money",
	"forex",
	"payments",
	"none",
	"other",
	"trading",
]);
const CustomerExtraAPI = z.union([
	z.object({
		individual: z.object({
			currency_extra: z.array(IndividualCurrencyExtra),
			date_of_birth: z.string().regex(DOB_REGEX),
			first_name: z.string(),
			gender: Gender,
			identification: z.union([z.null(), IndividualIdentification]).optional(),
			last_name: z.string(),
			middle_name: z.union([z.string(), z.null()]).optional(),
			nationality: z.string().min(2).max(2),
		}),
	}),
	z.object({
		business: z.object({
			business_name: z.string(),
			country_of_issuance: z.string().min(2).max(2),
			currency_extra: z.array(BusinessCurrencyExtra),
			issuance_authority: z.union([z.string(), z.null()]).optional(),
			registration_date: z.union([z.string(), z.null()]).optional(),
			registration_number: z.string(),
			regulatory_licenses: z.array(RegulatoryLicenseType),
		}),
	}),
]);
const DocumentType = z.enum([
	"passport",
	"visa",
	"national_id",
	"trade_licence",
	"commercial_licence",
	"identity_verification_biometrics",
	"incorporation_cert",
	"incumbency_cert",
	"memorandum_articles",
	"bank_statement",
	"photo",
	"proof_of_address",
	"regulatory_license",
	"articles_of_association",
	"source_of_funds",
	"aml_cft_sanctions_ind_audit",
	"aml_cft_sanctions_policy",
	"compliance_officer_resume",
	"financial_statements",
	"share_certificate",
	"corporate_resolution",
	"wolfsberg_gcbddq",
	"edd_concentration_form",
	"w8_ben_e",
	"other",
]);
const DocumentSummary = z.object({
	document_type: DocumentType,
	id: z.string(),
	reference: z.string().max(35),
});
const RejectionReason = z.enum(["prohibited", "sanction", "pep", "financial_crimes", "other"]);
const Customer = CustomerExtraAPI.and(
	z.object({
		address: Address,
		approval_status: ApprovalStatus,
		display_name: z.string(),
		documents: z.array(DocumentSummary),
		id: z.string(),
		reference: z.string().max(36),
		rejection_reason: z.union([z.null(), RejectionReason]).optional(),
		relationship_type: CustomerRelationshipType,
	}),
);
const ComplianceProfile = z
	.object({
		client_profile_and_business_model: z.union([z.string(), z.null()]),
		monthly_volume: z.union([z.number(), z.null()]),
		number_of_transactions: z.union([z.number(), z.null()]),
		purpose_of_transactions: z.union([z.null(), PurposeCode]),
	})
	.partial();
const AgreementAcceptance = z.object({
	ip_address: z.string(),
	signed_at: z.string().datetime(),
});
const IdentityCheck = z.object({
	date_of_birth: z.string().regex(DOB_REGEX),
	first_name: z.string(),
	gender: z.string(),
	issuing_country: z.string().min(2).max(2),
	last_name: z.string(),
	nationality: z.string().min(2).max(2),
});
const VerificationResult = z.enum(["clear", "needs_review"]);
const PhotoCheck = z.object({
	face_comparison: VerificationResult,
	image_integrity: VerificationResult,
	visual_authenticity: VerificationResult,
});
const DocumentCheck = z.object({
	age_validation: VerificationResult,
	data_comparison: VerificationResult,
	date_of_expiry: z.string(),
	digital_tampering: VerificationResult,
	document_numbers: z.array(z.string()),
	document_type: IdDocumentType,
	image_integrity: VerificationResult,
	visual_authenticity: VerificationResult,
});
const WatchlistRecord = z.object({
	comment: z.string(),
	list_types: z.array(z.string()),
	name: z.string(),
	status: z.string(),
	vendor_hit_id: z.string(),
});
const WatchlistCheck = z.object({
	adverse_media: VerificationResult,
	monitored_lists: VerificationResult,
	politically_exposed_person: VerificationResult,
	records: z.array(WatchlistRecord),
	sanction: VerificationResult,
});
const IdentificationResult = z.object({
	issuing_country: z.string().min(2).max(2),
	result: VerificationResult,
	type: IdDocumentType,
});
const IdentificationsCheck = z.object({ results: z.array(IdentificationResult) });
const ComplianceCheckDetails = z.union([
	IdentityCheck.and(z.object({ type: z.literal("identity") })),
	PhotoCheck.and(z.object({ type: z.literal("photo") })),
	DocumentCheck.and(z.object({ type: z.literal("document") })),
	WatchlistCheck.and(z.object({ type: z.literal("watchlist") })),
	z.object({ type: z.literal("address") }),
	z.object({ type: z.literal("date_of_birth") }),
	IdentificationsCheck.and(z.object({ type: z.literal("identifications") })),
	z.object({ type: z.literal("business_registration") }),
	z.object({ type: z.literal("business_status") }),
	z.object({ type: z.literal("ownership") }),
]);
const ComplianceCheck = ComplianceCheckDetails.and(
	z.object({
		completed_at: z.string().datetime(),
		initiated_at: z.string().datetime(),
		result: VerificationResult,
	}),
);
const RiskLevel = z.enum(["low", "medium", "high"]);
const ComplianceVerifications = z.object({
	checks: z.array(ComplianceCheck).min(1),
	completed_at: z.string().datetime(),
	ip_addresses: z.array(z.string()).min(1),
	review_reason: z.string(),
	reviewed_at: z.string().datetime(),
	reviewed_by: z.string(),
	risk_level: RiskLevel,
	risk_score: z.number().int().gte(0),
});
const Disclosures = z
	.object({
		affiliated_firm: z.union([z.string(), z.null()]),
		controlling_firms: z.array(z.string()),
		immediate_family_exposed: z.union([z.boolean(), z.null()]),
		is_affiliated_exchange_or_finra: z.union([z.boolean(), z.null()]),
		is_control_person: z.union([z.boolean(), z.null()]),
		is_exempt_from_tax_under_501a: z.union([z.boolean(), z.null()]),
		is_participating_fatca: z.union([z.boolean(), z.null()]),
		is_politically_exposed: z.union([z.boolean(), z.null()]),
	})
	.partial();
const FinancialProfile = z.object({
	annual_income_max: z.number().int().gte(0),
	annual_income_min: z.number().int().gte(0),
	liquid_net_worth_max: z.number().int().gte(0),
	liquid_net_worth_min: z.number().int().gte(0),
	total_net_worth_max: z.number().int().gte(0),
	total_net_worth_min: z.number().int().gte(0),
});
const FundingSource = z.enum([
	"employment_income",
	"investments",
	"inheritance",
	"business_income",
	"savings",
	"family",
	"business_revenue",
	"asset_appreciation",
	"sales_of_assets",
	"investors",
	"other",
]);
const Industry = z.enum([
	"administration_or_public_relations",
	"agriculture",
	"architecture",
	"arts_or_film_or_music",
	"construction",
	"design",
	"education",
	"finance",
	"food_production",
	"government_or_public_services",
	"healthcare",
	"legal_services",
	"media_or_marketing",
	"oil_or_energy_or_mining",
	"pharmaceuticals_or_biochemicals",
	"real_estate",
	"retail_restaurants",
	"technology",
	"sports_or_sports_medicine_or_fitness",
	"tourism",
	"transport",
	"other",
]);
const LegalEntityType = z.enum([
	"banking_institution",
	"broker_or_dealer",
	"c_corporation",
	"foreign_financial_institution",
	"fund_or_hedge_fund",
	"general_partnership",
	"irrevocable_trust",
	"limited_liability_corporation",
	"limited_partnership",
	"revocable_trust",
	"s_corporation",
	"unincorporated",
	"other",
]);
const TaxIdType = z.enum(["USA_SSN", "USA_EIN", "USA_ITIN", "USA_TIN", "ARE_TRN"]);
const TaxId = z.object({ number: z.string(), tax_id_type: TaxIdType });
const AlpacaOnboardingData = z
	.object({
		certifications_agreement: z.union([z.null(), AgreementAcceptance]),
		compliance_verifications: z.union([z.null(), ComplianceVerifications]),
		country_of_tax_residence: z.union([z.string(), z.null()]),
		disclosures: z.union([z.null(), Disclosures]),
		email: z.union([z.string(), z.null()]),
		financial_profile: z.union([z.null(), FinancialProfile]),
		funding_sources: z.union([z.array(FundingSource), z.null()]),
		funding_sources_other: z.union([z.string(), z.null()]),
		industry: z.union([z.null(), Industry]),
		industry_other: z.union([z.string(), z.null()]),
		legal_entity_type: z.union([z.null(), LegalEntityType]),
		legal_entity_type_other: z.union([z.string(), z.null()]),
		phone_number: z.union([z.string(), z.null()]),
		state_of_incorporation: z.union([z.string(), z.null()]),
		tax_id: z.union([z.null(), TaxId]),
	})
	.partial();
const DocumentsStatus = z.enum(["submitted", "not_provided", "not_applicable"]);
const GetCustomerInternalResponse = Customer.and(ComplianceProfile)
	.and(AlpacaOnboardingData)
	.and(z.object({ documents_status: DocumentsStatus }));
const UpdateCustomerApprovalStatusRequest = z.object({
	approval_status: ApprovalStatus,
	rejection_reason: z.union([z.null(), RejectionReason]).optional(),
});
const Product = z.enum(["clearing", "cash_management"]);
const ManuallyCompleteOnboarding = z.object({
	bank: z.string(),
	currency: Currency,
	customer_bank_id: z.string(),
	onboarding_data: z.union([z.object({}).partial(), z.null()]).optional(),
	product: Product.optional(),
});
const ErrorResponse = z.object({ code: z.string(), error: z.string(), message: z.string() });
const LinkDocumentRequest = z.object({ document_id: z.string() });
const ProductOnboarding = z.object({
	currency: Currency,
	customer_id: z.string(),
	product: Product,
	status: OnboardingStatus,
});
const OnboardToProduct = z.object({ command_id: z.string() });
const OverwriteOnboarding = z.object({
	bank: z.string(),
	command_id: z.string(),
	currency: Currency,
	customer_bank_id: z.string(),
	onboarding_data: z.union([z.object({}).partial(), z.null()]).optional(),
	product: Product.optional(),
});
const ReflectOnboarding = z.object({
	bank: z.string(),
	currency: Currency,
	customer_bank_id: z.string(),
	onboarding_data: z.union([z.object({}).partial(), z.null()]).optional(),
	product: Product.optional(),
});
const RetryOnboarding = z.object({ command_id: z.string(), currency: Currency });
const UpdateShareholderRequest = z.object({ title: z.union([z.string(), z.null()]) }).partial();
const UploadDocumentRequest = z.object({
	description: z.string(),
	document_type: DocumentType,
	file: typeof window === "undefined" ? z.any() : z.instanceof(File),
	reference: z.string().max(35),
});
const UploadDocumentResponse = z.object({ document_id: z.string() });
const Document = z.object({
	checksum: z.string(),
	document_type: DocumentType,
	id: z.string(),
	reference: z.string().max(35),
	size_bytes: z.union([z.number(), z.null()]).optional(),
	url: z.string(),
});
const Direction = z.enum(["Inbound", "Outbound"]);
const WriteOverrideFeeType = z.union([
	z.object({
		PrimaryAccount: z.object({ direction: Direction, fee: LocalWriteFee }),
	}),
	z.object({
		AccountId: z.object({
			account_id: z.string(),
			direction: Direction,
			fee: LocalWriteFee,
		}),
	}),
]);
const UpdateFeeSpec = z.object({
	apr_tiers: z.union([z.object({}).partial(), z.null()]).optional(),
	cash_management_transfers: z.union([z.object({}).partial(), z.null()]).optional(),
	iban: z.union([z.null(), IbanWriteFee]).optional(),
	monthly_minimum: z.union([z.array(MonthlyMinimum), z.null()]).optional(),
	override_fees: z.union([z.record(z.array(WriteOverrideFeeType)), z.null()]).optional(),
	transaction: z.record(WriteFeeSpecCondition),
});
const PatchFeeRequest = ForexEntries.and(
	z.object({
		fee_spec: UpdateFeeSpec,
		nexus_subscriber: z.union([z.boolean(), z.null()]).optional(),
	}),
);
const CreateInternalTransferToPrimaryAccountRequest = z.object({
	amount: z.number().int().gte(0),
	external_id: z.union([z.string(), z.null()]).optional(),
	from_account_id: z.string(),
	reference: z.string().max(100),
});
const CreateInternalTransferToPrimaryAccountResponse = z.object({ transaction_id: z.string() });
const OnboardOrganisationRequest = z.object({
	currency: Currency,
	force: z.union([z.boolean(), z.null()]).optional(),
	internal_account_id: z.string(),
});
const OnboardOrganisationResponse = z.object({
	bank: z.string(),
	currency: Currency,
	internal_account_id: z.string(),
});
const OrganisationOnboardingEntry = z.object({
	bank: z.string(),
	currency: Currency,
	internal_account_id: z.string(),
});
const OrganisationOnboardingsResponse = z.object({
	onboardings: z.array(OrganisationOnboardingEntry),
});
const CreateAccountRequest = z.object({
	account_name: z.string().max(140),
	account_type: CreateAccountType,
	currency: Currency,
	external_id: z.string().max(36),
});
const Month = z.object({ month: z.number().int().gte(0), year: z.number().int() });
const MonthlyStatementStatus = z.enum(["not_requested", "scheduled", "completed", "rejected"]);
const MonthlyStatementProgress = Month.and(
	z.object({
		document_id: z.union([z.string(), z.null()]).optional(),
		document_url: z.union([z.string(), z.null()]).optional(),
		report_id: z.string(),
		requested_at: z.union([z.string(), z.null()]).optional(),
		status: MonthlyStatementStatus,
	}),
);
const GetMonthlyStatementsResponse = z.object({
	completed: z.number().int().gte(0),
	done: z.boolean(),
	months: z.array(MonthlyStatementProgress),
	organisation_id: z.string(),
	pending: z.number().int().gte(0),
	total: z.number().int().gte(0),
});
const RequestMonthlyStatementsRequest = z.object({
	end: Month,
	force: z.boolean().optional(),
	start: z.union([z.null(), Month]).optional(),
});
const RequestedMonth = Month.and(
	z.object({
		document_id: z.string(),
		error: z.union([z.string(), z.null()]).optional(),
		report_id: z.string(),
	}),
);
const RequestMonthlyStatementsResponse = z.object({
	end: Month,
	months: z.array(RequestedMonth),
	organisation_id: z.string(),
	requested: z.number().int().gte(0),
	start: Month,
	total: z.number().int().gte(0),
});
const SubAccountType = z.union([
	z.literal("client_money"),
	z.literal("suspense"),
	z.literal("profit_and_loss"),
	z.literal("fx_profit_and_loss"),
	z.literal("fx_settlement"),
	z.object({ fx_settlement_for_organisation: z.string() }),
	z.literal("cash_management_settlement"),
]);
const ReflectSystemAccountAsCashManagementRequest = z.object({
	account_name: z.string().max(140),
	account_owner: z.string().max(140),
	account_type: SubAccountType,
	bank_external_id: z.string(),
	command_id: z.string(),
	currency: z.string(),
	customer_id: z.string(),
	internal_account_id: z.string(),
});
const ReflectSystemAccountAsCashManagementResponse = z.object({ account_id: z.string() });
const ReflectReturnedPaymentRequest = z.object({
	bank_return_date: z.union([z.string(), z.null()]).optional(),
	command_id: z.string(),
	orginal_bank_payment_id: z.union([z.string(), z.null()]).optional(),
});
const ReflectReturnedPaymentResponse = z.object({ transaction_id: z.string() });
const ReflectExecutedRequest = z.object({ command_id: z.string() });
const ReflectFailedRequest = z.object({ command_id: z.string(), reason: z.string() });
const ReflectSubmittedRequest = z.object({
	bank_data: z.unknown().optional(),
	command_id: z.string(),
	external_id: z.string(),
	timestamp: z.union([z.string(), z.null()]).optional(),
});
const UnonboardOrganisationRequest = z.object({
	currency: Currency,
	internal_account_id: z.string(),
});
const TransactionsStatementPayload = z.object({
	from: z.string().datetime(),
	to: z.string().datetime(),
});
const MonthlyStatementPayload = z.object({
	month: z.number().int().gte(0),
	year: z.number().int(),
});
const payload = z
	.union([
		TransactionsStatementPayload.and(
			z.object({ report_type: z.literal("transactions_statement") }),
		),
		MonthlyStatementPayload.and(z.object({ report_type: z.literal("monthly_statement") })),
	])
	.optional();
const ResourcePayload = z.union([
	TransactionsStatementPayload.and(
		z.object({ report_type: z.literal("transactions_statement") }),
	),
	MonthlyStatementPayload.and(z.object({ report_type: z.literal("monthly_statement") })),
]);
const ReportStatus = z.enum(["scheduled", "completed", "rejected"]);
const ReportResponse = ResourcePayload.and(
	z.object({
		document_id: z.union([z.string(), z.null()]).optional(),
		id: z.string(),
		status: ReportStatus,
	}),
);
const ListReportsPagedResponse = z.object({
	pages: PageMetadata,
	results: z.array(ReportResponse),
});
const RetryPendingSubAccountsRequest = z.object({ account_ids: z.array(z.string()) });
const RetrySubAccountOutcome = z.union([
	z.object({ status: z.literal("retried") }),
	z.object({ message: z.string(), status: z.literal("error") }),
]);
const RetryPendingSubAccountsResponse = z.object({ outcomes: z.record(RetrySubAccountOutcome) });
const Permission = z.object({ description: z.string(), name: z.string() });
const SelectedOptionRequest = z.object({
	detail: z.union([z.number(), z.null()]).optional(),
	value: z.string(),
});
const AnswerValueRequest = z.union([
	z.object({ amount: z.number().int().gte(0), type: z.literal("amount") }),
	z.object({ type: z.literal("choice"), value: z.string() }),
	z.object({
		selected: z.array(SelectedOptionRequest),
		type: z.literal("choices"),
	}),
]);
const SubmittedAnswerRequest = z.object({ question_code: z.string(), value: AnswerValueRequest });
const RequestAdvisoryRequest = z.object({ answers: z.array(SubmittedAnswerRequest) });
const SleeveResponse = z.enum(["cash", "bills"]);
const SleeveTargetResponse = z.object({
	sleeve: SleeveResponse,
	target_bps: z.number().int().gte(0),
});
const RungResponse = z.object({
	holdings: z.array(SleeveTargetResponse),
	month: z.number().int().gte(0),
});
const LadderResponse = z.object({
	rungs: z.array(RungResponse),
	spacing_months: z.number().int().gte(0),
});
const RecommendationResponse = z.object({
	disclosures: z.array(z.string()),
	disclosures_triggered: z.array(z.string()),
	ladder_schedule: z.union([z.null(), LadderResponse]).optional(),
	liquidation_waterfall: z.array(SleeveResponse),
	model_version: z.string(),
	name: z.string(),
	portfolio_id: z.string(),
	sweep_insurance_notice: z.boolean(),
	target_allocation: z.array(SleeveTargetResponse),
});
const AdvisoryResponse = z.object({
	customer_id: z.string(),
	inputs_snapshot: z.array(SubmittedAnswerRequest),
	recommendation: RecommendationResponse,
});
const AdvisoryStatusRequest = z.literal("accepted");
const AdvisoryDecisionRequest = z.object({ status: AdvisoryStatusRequest });
const AdvisoryStatusResponse = z.literal("accepted");
const AdvisoryDecisionResponse = z.object({
	customer_id: z.string(),
	status: AdvisoryStatusResponse,
});
const DetailSpecResponse = z.object({ max: z.number().int(), min: z.number().int() });
const AnswerOptionResponse = z.object({
	detail: z.union([z.null(), DetailSpecResponse]).optional(),
	is_exclusive: z.boolean(),
	label: z.string(),
	value: z.string(),
});
const AnswerSpecResponse = z.union([
	z.object({
		options: z.array(AnswerOptionResponse),
		type: z.literal("single_choice"),
	}),
	z.object({
		options: z.array(AnswerOptionResponse),
		type: z.literal("multiple_choice"),
	}),
	z.object({ max: z.number().int().gte(0), type: z.literal("positive_int") }),
]);
const QuestionResponse = z.object({
	answer: AnswerSpecResponse,
	code: z.string(),
	is_required: z.boolean(),
	order: z.number().int().gte(0),
	prompt: z.string(),
});
const QuestionnaireResponse = z.object({ id: z.string(), questions: z.array(QuestionResponse) });
const transaction_Batch = z.object({
	batch_id: z.string(),
	batch_sequence_no: z.number().int().gte(0),
});
const BeneficiaryDetails = z.object({
	identifier: AccountDetailsIdentifier,
	name: z.string().max(140),
});
const BeneficiaryExtraDetails = z
	.object({
		address: z.union([z.null(), Address]),
		bic_code: z.union([z.string(), z.null()]),
	})
	.partial();
const PaymentFailure = z.union([
	z.object({ bank_error: z.string() }),
	z.literal("insufficient_funds"),
	z.literal("internal_accounts_differ"),
	z.object({ system_error: z.string() }),
	z.object({ compliance_error: z.string() }),
	z.literal("account_frozen"),
]);
const FeeSourceRef = z.union([
	z.object({ type: z.literal("included_in_payment") }),
	z.object({ type: z.literal("primary_account") }),
]);
const RemitterDetails = z.object({
	identifier: AccountDetailsIdentifier,
	name: z.string().max(140),
});
const TransactionState = z.enum(["Created", "Submitted", "Executed", "Failed", "Settled"]);
const LocalPayment = z.object({
	account_id: z.string(),
	amount_with_fee: z.number().int().gte(0),
	authorised_at: z.union([z.string(), z.null()]).optional(),
	batch: z.union([z.null(), transaction_Batch]).optional(),
	beneficiary: BeneficiaryDetails,
	beneficiary_extra: z.union([z.null(), BeneficiaryExtraDetails]).optional(),
	created_at: z.string().regex(DOB_REGEX),
	currency: Currency,
	direction: Direction,
	executed_at: z.union([z.string(), z.null()]).optional(),
	external_id: z.union([z.string(), z.null()]).optional(),
	external_reference: z.string(),
	failed_at: z.union([z.string(), z.null()]).optional(),
	failure_reason: z.union([z.null(), PaymentFailure]).optional(),
	fee_amount: z.number().int().gte(0),
	fee_source: z.union([z.null(), FeeSourceRef]).optional(),
	id: z.string(),
	instructed_amount: z.number().int().gte(0),
	purpose: PurposeCode,
	remitter: RemitterDetails,
	scheme: z.union([z.null(), PaymentScheme]).optional(),
	state: TransactionState,
	submitted_at: z.union([z.string(), z.null()]).optional(),
});
const LocalReturnPayment = z.object({
	account_id: z.string(),
	beneficiary: BeneficiaryDetails,
	created_at: z.string().regex(DOB_REGEX),
	currency: Currency,
	direction: Direction,
	external_reference: z.string(),
	id: z.string(),
	instructed_amount: z.number().int().gte(0),
	reason: z.string().max(140),
	remitter: RemitterDetails,
	returned_id: z.string(),
	state: TransactionState,
});
const InternalTransferTransactionState = z.enum(["Created", "Booked", "Failed", "Settled"]);
const InternalTransfer = z.object({
	account_id: z.string(),
	counterpart_account_id: z.string(),
	created_at: z.string().regex(DOB_REGEX),
	currency: Currency,
	direction: Direction,
	external_id: z.union([z.string(), z.null()]).optional(),
	external_reference: z.string(),
	failed_at: z.union([z.string(), z.null()]).optional(),
	failure_reason: z.union([z.null(), PaymentFailure]).optional(),
	id: z.string(),
	instructed_amount: z.number().int().gte(0),
	settled_at: z.union([z.string(), z.null()]).optional(),
	state: InternalTransferTransactionState,
});
const FxTransferTransactionState = z.enum([
	"Created",
	"Submitted",
	"Executed",
	"Failed",
	"Settled",
]);
const FxTransfer = z.object({
	account_id: z.string(),
	counterpart_account_id: z.string(),
	counterpart_amount: z.number().int().gte(0),
	counterpart_currency: Currency,
	created_at: z.string().regex(DOB_REGEX),
	currency: Currency,
	direction: Direction,
	executed_at: z.union([z.string(), z.null()]).optional(),
	external_reference: z.string(),
	failed_at: z.union([z.string(), z.null()]).optional(),
	failure_reason: z.union([z.null(), PaymentFailure]).optional(),
	id: z.string(),
	instructed_amount: z.number().int().gte(0),
	rate: z.number(),
	state: FxTransferTransactionState,
	submitted_at: z.union([z.string(), z.null()]).optional(),
});
const FeeTargetRef = z.object({ reference: z.string(), type: z.literal("Transaction") });
const FeeDebit = z.object({
	account_id: z.string(),
	amount: z.number().int().gte(0),
	currency: Currency,
	fee_target: FeeTargetRef,
	id: z.string(),
	timestamp: z.string().regex(DOB_REGEX),
});
const Transaction = z.union([
	LocalPayment.merge(z.object({ type: z.literal("local_payment") })),
	LocalReturnPayment.merge(z.object({ type: z.literal("local_return_payment") })),
	InternalTransfer.merge(z.object({ type: z.literal("internal_transfer") })),
	FxTransfer.merge(z.object({ type: z.literal("f_x_transfer") })),
	FeeDebit.merge(z.object({ type: z.literal("fee_debit") })),
]);
const ListAllTransactionsPagedResponse = z.object({
	pages: Cursor_String,
	results: z.array(Transaction),
});
const AuthorizeBatchPaymentResponse = z.object({ batch_id: z.string(), redirect_url: z.string() });
const BatchDryRunSummary = z.object({ number_of_payments: z.number().int().gte(0) });
const TierBasis = z.union([
	z.object({ type: z.literal("amount") }),
	z.object({ amount: z.number().int().gte(0), type: z.literal("volume") }),
	z.object({
		amount: z.number().int().gte(0),
		type: z.literal("transaction_count"),
	}),
	z.object({ type: z.literal("external") }),
	z.object({ type: z.literal("exempt") }),
]);
const FeeThreshold = z.enum(["Min", "Max"]);
const VariableFee = z.object({ amount: z.number(), percentage: z.number() });
const Vat = z.object({ amount: z.number(), percentage: z.number() });
const FeeCalculation = z.object({
	base_fee: z.number(),
	conversion_rate: z.number(),
	fee: z.number(),
	fee_basis: TierBasis,
	fee_threshold_override: z.union([z.null(), FeeThreshold]).optional(),
	instructed_amount: z.number(),
	variable_fee: z.union([z.null(), VariableFee]).optional(),
	vat: z.union([z.null(), Vat]).optional(),
});
const RetryTransferResponse = z.object({ operation_id: z.string() });
const RegisterUserRequest = z.object({
	api_enabled: z.union([z.boolean(), z.null()]).optional(),
	fedwire_enabled: z.union([z.boolean(), z.null()]).optional(),
	fee_source_account: z.union([z.null(), FeeSourceAccount]).optional(),
	maker_checker_enabled: z.union([z.boolean(), z.null()]).optional(),
	onboarding_mode: z.union([z.null(), OnboardingMode]).optional(),
	organisation_name: z.union([z.string(), z.null()]).optional(),
	user_id: z.string(),
});
const RegisterUserResponse = z.object({ org_id: z.string() });
const Organisation = z.object({
	auth0_org_id: z.string(),
	display_name: z.string(),
	name: z.string(),
	org_id: z.string(),
});
const AccountTypeV2 = z.enum(["primary", "virtual"]);
const SharedPendingReason = z.enum([
	"not_requested",
	"awaiting_bank_response",
	"awaiting_details_propagation",
	"awaiting_ledger",
	"rejected",
]);
const ZenusPendingReason = z.enum([
	"zenus_awaiting_vrn",
	"zenus_awaiting_activation",
	"zenus_awaiting_activation_and_vrn",
	"zenus_fi_awaiting_activations",
	"zenus_fi_awaiting_activations_and_vrn",
	"zenus_fi_awaiting_second_activation",
	"zenus_fi_awaiting_second_activation_and_vrn",
]);
const PendingReason = z.union([SharedPendingReason, ZenusPendingReason]);
const PendingAccount = z.object({
	account_name: z.string().max(140),
	account_owner: z.string().max(140),
	account_type: AccountTypeV2,
	bank: z.union([z.string(), z.null()]).optional(),
	currency: Currency,
	customer_id: z.string(),
	external_id: z.string(),
	id: z.string(),
	organisation_id: z.string(),
	pending_reason: PendingReason,
	requested_at: z.union([z.string(), z.null()]).optional(),
});
const ListPendingAccountsPagedResponse = z.object({
	pages: Cursor_String,
	results: z.array(PendingAccount),
});
const CreateAccountRequestV2 = z.object({
	account_name: z.string().max(140),
	currency: Currency,
	external_id: z.string().max(36),
});
const CreateAccountResponse = z.object({ account_id: z.string() });
const ReadFeeSpecCondition = z.union([
	z.object({
		InAndOut: z.object({ inbound: LocalReadFee, outbound: LocalReadFee }),
	}),
	z.object({ ByCurrency: z.object({ fee: LocalReadFee }) }),
]);
const ReadFeeSpecV4 = z.union([
	z.object({
		fee_calculation: z.literal("fuse"),
		fees: z.record(ReadFeeSpecCondition),
		iban: z.union([z.null(), IbanReadFee]).optional(),
		monthly_minimum: z.union([z.array(MonthlyMinimum), z.null()]).optional(),
		override_fees: z.union([z.object({}).partial(), z.null()]).optional(),
	}),
	z.object({ fee_calculation: z.literal("solvimon") }),
]);
const ReadOrgFeeResponseV4 = ForexEntries.and(
	z.object({
		apr_tiers: z.union([z.object({}).partial(), z.null()]).optional(),
		cash_management_transfers: z.union([z.object({}).partial(), z.null()]).optional(),
		nexus_subscriber: z.boolean().optional(),
		transaction: ReadFeeSpecV4,
	}),
);
const OrganisationFeesV2 = z.object({ fees: ReadOrgFeeResponseV4, organisation_id: z.string() });
const AllOrganisationsFeesResponseV2 = z.object({ organisation_fees: z.array(OrganisationFeesV2) });
const ReadOrgFeeResponseV2 = ForexEntries.and(
	z.object({
		iban: z.union([z.null(), IbanReadFee]).optional(),
		monthly_minimum: z.union([z.array(MonthlyMinimum), z.null()]).optional(),
		transaction: z.record(LocalReadFee),
	}),
);
const ReadFeeSpecV3 = z.union([
	z.object({
		fee_calculation: z.literal("fuse"),
		fees: z.record(LocalReadFee),
		iban: z.union([z.null(), IbanReadFee]).optional(),
		monthly_minimum: z.union([z.array(MonthlyMinimum), z.null()]).optional(),
	}),
	z.object({ fee_calculation: z.literal("solvimon") }),
]);
const ReadOrgFeeResponseV3 = ForexEntries.and(
	z.object({
		nexus_subscriber: z.boolean().optional(),
		transaction: ReadFeeSpecV3,
	}),
);
const SearchAccountsPagedResponse = z.object({ pages: PageMetadata, results: z.array(Account) });
const EstimateFeeForBeneficiaryRequest = z.object({
	amount: z.number().int().gte(0),
	beneficiary: AccountDetailsIdentifier,
});
const EstimateFeeForBeneficiaryResponse = z.object({
	account_id: z.string(),
	amount_with_fee: z.number().int().gte(0),
	currency: Currency,
	estimated_at: z.string().regex(DOB_REGEX),
	fee_amount: z.number().int().gte(0),
	instructed_amount: z.number().int().gte(0),
	nexus: z.boolean(),
});
const CashManagementInterestState = z.union([
	z.object({ apr_tier_name: z.string(), status: z.literal("active") }),
	z.object({
		apr_tier_name: z.union([z.string(), z.null()]).optional(),
		status: z.literal("pending_change"),
	}),
	z.object({ status: z.literal("inactive") }),
]);
const CurrencyInterest = CashManagementInterestState.and(
	z.object({
		account_rate_bps: z.union([z.number(), z.null()]).optional(),
		accrued_as_of: z.union([z.string(), z.null()]).optional(),
		accrued_interest: z.string(),
	}),
);
const CashManagementInterest = z.object({ usd: CurrencyInterest });
const GetCashManagementAccountResponse = z.object({
	account_name: z.string(),
	account_owner: z.string(),
	account_type: z.string(),
	available_balance: z.number().int().gte(0),
	currency: z.string(),
	current_balance: z.number().int().gte(0),
	customer_id: z.string(),
	id: z.string(),
	interest: CashManagementInterest,
	status: z.string(),
});
const CashManagementCashLimitsResponse = z.object({
	cash: z.number().int().gte(0),
	cash_transferable: z.number().int().gte(0),
	cash_withdrawable: z.number().int().gte(0),
	currency: Currency,
});
const CashInterestStatusRequest = z.enum(["active", "inactive"]);
const CashInterestProgramRequest = z.object({ status: CashInterestStatusRequest });
const RequestCashInterestChangeRequest = z
	.object({ usd: z.union([z.null(), CashInterestProgramRequest]) })
	.partial();
const RequestCashInterestChangeResponse = z.object({}).partial();
const FundIdentifier = z.string();
const InvestmentAmount = z.object({
	amount: z.number().int().gte(0),
	type: z.literal("face_value"),
});
const CreateBuyOrderRequest = z.object({
	cash_currency: Currency,
	fund_id: FundIdentifier,
	investment_amount: InvestmentAmount,
});
const CreateBuyOrderResponse = z.object({ buy_order_id: z.string() });
const GetMarketClockResponse = z.object({
	accepting_orders: z.boolean(),
	accepting_orders_until: z.union([z.string(), z.null()]).optional(),
	reopens_at: z.union([z.string(), z.null()]).optional(),
	timestamp: z.string().regex(DOB_REGEX),
});
const CreateSellOrderRequest = z.object({
	amount_of_shares: z.number().int().gte(1),
	cash_currency: Currency,
	fund_id: FundIdentifier,
});
const CreateSellOrderResponse = z.object({ sell_order_id: z.string() });
const CashManagementAssetClass = z.enum(["us_treasury", "us_corporate"]);
const asset_class = z.union([z.null(), CashManagementAssetClass]).optional();
const CashManagementBondType = z.enum([
	"bill",
	"bond",
	"note",
	"strips",
	"tips",
	"floating",
	"unknown",
]);
const bond_type = z.union([z.null(), CashManagementBondType]).optional();
const CashManagementBondStatus = z.enum([
	"called",
	"defaulted",
	"liquidated",
	"matured",
	"outstanding",
	"pre_issuance",
	"repurchased",
	"restructured",
	"unknown",
]);
const CashManagementCorporateDetails = z.object({
	accrued_interest: z.union([z.string(), z.null()]).optional(),
	callable: z.boolean(),
	issue_minimum_denomination: z.number().int().gte(0),
	issuer: z.string(),
	par_value: z.number().int().gte(0),
	perpetual: z.boolean(),
	sector: z.string(),
	seniority: z.string(),
	ticker: z.string(),
});
const CashManagementCouponFrequency = z.enum([
	"annual",
	"monthly",
	"quarterly",
	"semi_annual",
	"unspecified",
	"zero",
]);
const CashManagementCouponType = z.enum([
	"complex",
	"fixed",
	"floating",
	"unspecified",
	"variable",
	"zero",
]);
const CashManagementAssetSource = z.literal("alpaca");
const CashManagementAsset = z.object({
	asset_class: CashManagementAssetClass,
	bond_status: CashManagementBondStatus,
	bond_type: z.union([z.null(), CashManagementBondType]).optional(),
	close_price: z.union([z.string(), z.null()]).optional(),
	close_price_date: z.union([z.string(), z.null()]).optional(),
	close_yield_to_maturity: z.union([z.string(), z.null()]).optional(),
	close_yield_to_worst: z.union([z.string(), z.null()]).optional(),
	corporate: z.union([z.null(), CashManagementCorporateDetails]).optional(),
	coupon: z.string(),
	coupon_frequency: CashManagementCouponFrequency,
	coupon_type: CashManagementCouponType,
	description: z.string(),
	id: z.string(),
	isin: z.string(),
	issue_date: z.string(),
	maturity_date: z.union([z.string(), z.null()]).optional(),
	name: z.string(),
	next_coupon_date: z.union([z.string(), z.null()]).optional(),
	price: z.union([z.string(), z.null()]).optional(),
	source: CashManagementAssetSource,
	tradable: z.boolean(),
});
const ListCashManagementAssetsResponse = z.object({
	pages: PageMetadata,
	results: z.array(CashManagementAsset),
});
const CashManagementPositionStatus = z.enum(["open", "closed"]);
const status = z.union([z.null(), CashManagementPositionStatus]).optional();
const CashManagementPosition = z.object({
	asset_class: CashManagementAssetClass,
	cash_management_account_id: z.string(),
	coupon: z.string(),
	coupon_frequency: CashManagementCouponFrequency,
	fund_id: z.string(),
	id: z.string(),
	isin: z.string(),
	maturity_date: z.union([z.string(), z.null()]).optional(),
	name: z.string(),
	price: z.union([z.string(), z.null()]).optional(),
	status: CashManagementPositionStatus,
	total_shares_amount: z.number().int().gte(0),
});
const ListCashManagementPositionsResponse = z.object({
	pages: PageMetadata,
	results: z.array(CashManagementPosition),
});
const TransferAccountType = z.enum(["account", "cash_management_account"]);
const CreateTransferRequest = z.object({
	amount: z.number().int().gte(0),
	external_id: z.union([z.string(), z.null()]).optional(),
	external_reference: z.string().max(100),
	from_account_id: z.string(),
	from_account_type: TransferAccountType,
	to_account_id: z.string(),
	to_account_type: TransferAccountType,
});
const CreateTransferResponse = z.object({ transaction_id: z.string() });
const AddBusinessWithDocsRequest = z.object({
	address: Address,
	business_name: z.string().max(140),
	compliance_profile: z.union([z.null(), ComplianceProfile]).optional(),
	country_of_issuance: z.string(),
	currency_extra: z.union([z.array(BusinessCurrencyExtra), z.null()]).optional(),
	display_name: z.string().max(140),
	issuance_authority: z.union([z.string(), z.null()]).optional(),
	linked_documents: z.array(z.string()).optional(),
	reference: z.string().max(36),
	registration_date: z.union([z.string(), z.null()]).optional(),
	registration_number: z.string().max(140),
	regulatory_licenses: z.array(RegulatoryLicenseType).optional(),
	relationship_type: CustomerRelationshipType.optional().default("direct"),
});
const AddCustomerResponse = z.object({ id: z.string() });
const CustomerDryRunRequest = z
	.object({
		display_name: z.union([z.string(), z.null()]),
		registration_number: z.union([z.string(), z.null()]),
	})
	.partial();
const CustomerValidationDetails = z.object({
	customer_id: z.string(),
	display_name: z.string(),
	type: z.literal("existing_customer"),
});
const CustomerValidationField = z.enum(["display_name", "registration_number"]);
const CustomerValidationError = z.object({
	details: z.union([z.null(), CustomerValidationDetails]).optional(),
	field: CustomerValidationField,
	message: z.string(),
});
const CustomerDryRunResponse = z.object({ validation_errors: z.array(CustomerValidationError) });
const AddIndividualWithDocsRequest = z.object({
	address: Address,
	compliance_profile: z.union([z.null(), ComplianceProfile]).optional(),
	currency_extra: z.union([z.array(IndividualCurrencyExtra), z.null()]).optional(),
	date_of_birth: z.string().regex(DOB_REGEX),
	display_name: z.string().max(140),
	first_name: z.string().max(45),
	gender: z.union([z.null(), Gender]),
	identification: z.union([z.null(), IndividualIdentification]).optional(),
	last_name: z.string().max(45),
	linked_documents: z.array(z.string()).optional(),
	middle_name: z.union([z.string(), z.null()]).optional(),
	nationality: z.string().min(2).max(2),
	reference: z.string().max(36),
	relationship_type: CustomerRelationshipType.optional().default("direct"),
});
const SearchCustomersPagedResponse = z.object({
	pages: PageMetadata,
	results: z.array(CustomerSummary),
});
const OnboardingRequiredDocumentsStatus = z.object({
	missing_documents: z.array(DocumentType),
	uploaded_documents: z.array(DocumentType),
});
const InternalAccountType = z.enum(["pooled", "merchant", "operational"]);
const CreateInternalAccountRequest = z.object({
	account_identifier: AccountIdentifier,
	account_name: z.string().max(140),
	account_type: InternalAccountType,
	balance_tracking: z.boolean().optional(),
	bank: z.string(),
	currency: Currency,
	external_id: z.string(),
});
const CreateInternalAccountResponse = z.object({ account_id: z.string() });
const PortalBatch = z.object({
	batch_id: z.string(),
	batch_sequence_no: z.number().int().gte(0),
});
const PortalFeeSourceRef = z.union([
	z.object({ type: z.literal("included_in_payment") }),
	z.object({ type: z.literal("primary_account") }),
]);
const PortalRemitterDetails = z.object({
	identifier: AccountDetailsIdentifier,
	name: z.string().max(140),
});
const PortalLocalPayment = z.object({
	account_id: z.string(),
	amount_with_fee: z.number().int().gte(0),
	authorised_at: z.union([z.string(), z.null()]).optional(),
	batch: z.union([z.null(), PortalBatch]).optional(),
	beneficiary: BeneficiaryDetails,
	beneficiary_extra: z.union([z.null(), BeneficiaryExtraDetails]).optional(),
	created_at: z.string().regex(DOB_REGEX),
	currency: Currency,
	direction: Direction,
	executed_at: z.union([z.string(), z.null()]).optional(),
	external_id: z.union([z.string(), z.null()]).optional(),
	external_reference: z.string(),
	failed_at: z.union([z.string(), z.null()]).optional(),
	failure_reason: z.union([z.null(), PaymentFailure]).optional(),
	fee_amount: z.number().int().gte(0),
	fee_source: z.union([z.null(), PortalFeeSourceRef]).optional(),
	id: z.string(),
	instructed_amount: z.number().int().gte(0),
	nexus: z.boolean(),
	purpose: PurposeCode,
	remitter: PortalRemitterDetails,
	scheme: z.union([z.null(), PaymentScheme]).optional(),
	state: TransactionState,
	submitted_at: z.union([z.string(), z.null()]).optional(),
});
const PortalLocalReturnPayment = z.object({
	account_id: z.string(),
	beneficiary: BeneficiaryDetails,
	created_at: z.string().regex(DOB_REGEX),
	currency: Currency,
	direction: Direction,
	external_reference: z.string(),
	id: z.string(),
	instructed_amount: z.number().int().gte(0),
	reason: z.string().max(140),
	remitter: PortalRemitterDetails,
	returned_id: z.string(),
	state: TransactionState,
});
const PortalInternalTransfer = z.object({
	account_id: z.string(),
	account_name: z.string(),
	counterpart_account_id: z.string(),
	counterpart_account_name: z.union([z.string(), z.null()]).optional(),
	created_at: z.string().regex(DOB_REGEX),
	currency: Currency,
	direction: Direction,
	external_id: z.union([z.string(), z.null()]).optional(),
	external_reference: z.string(),
	failed_at: z.union([z.string(), z.null()]).optional(),
	failure_reason: z.union([z.null(), PaymentFailure]).optional(),
	id: z.string(),
	instructed_amount: z.number().int().gte(0),
	settled_at: z.union([z.string(), z.null()]).optional(),
	state: InternalTransferTransactionState,
});
const PortalFxTransfer = z.object({
	account_id: z.string(),
	account_name: z.string(),
	counterpart_account_id: z.string(),
	counterpart_account_name: z.union([z.string(), z.null()]).optional(),
	counterpart_amount: z.number().int().gte(0),
	counterpart_currency: Currency,
	created_at: z.string().regex(DOB_REGEX),
	currency: Currency,
	direction: Direction,
	executed_at: z.union([z.string(), z.null()]).optional(),
	external_reference: z.string(),
	failed_at: z.union([z.string(), z.null()]).optional(),
	failure_reason: z.union([z.null(), PaymentFailure]).optional(),
	id: z.string(),
	instructed_amount: z.number().int().gte(0),
	rate: z.number(),
	state: FxTransferTransactionState,
	submitted_at: z.union([z.string(), z.null()]).optional(),
});
const PortalFeeDebit = z.object({
	account_id: z.string(),
	amount: z.number().int().gte(0),
	currency: Currency,
	fee_target: FeeTargetRef,
	id: z.string(),
	timestamp: z.string().regex(DOB_REGEX),
});
const PortalTransaction = z.union([
	PortalLocalPayment.merge(z.object({ type: z.literal("local_payment") })),
	PortalLocalReturnPayment.merge(z.object({ type: z.literal("local_return_payment") })),
	PortalInternalTransfer.merge(z.object({ type: z.literal("internal_transfer") })),
	PortalFxTransfer.merge(z.object({ type: z.literal("f_x_transfer") })),
	PortalFeeDebit.merge(z.object({ type: z.literal("fee_debit") })),
]);
const SearchPortalTransactionsPagedResponse = z.object({
	pages: PageMetadata,
	results: z.array(PortalTransaction),
});
const AccountFields = z.object({
	account_name: z.string().max(140),
	account_owner: z.string().max(140),
	account_type: AccountTypeV2,
	currency: Currency,
	customer_id: z.union([z.string(), z.null()]).optional(),
	external_id: z.union([z.string(), z.null()]).optional(),
	id: z.string(),
});
const PendingAccountFields = AccountFields;
const AccountExtraFields = z.object({
	addressable_details: z.array(AddressableDetails),
	available_balance: z.number().int().gte(0),
	current_balance: z.number().int().gte(0),
	default_details: AccountDetailsIdentifier,
});
const OpenedAccountFields = AccountFields.and(AccountExtraFields);
const FailedAccountFields = AccountFields.and(z.object({ failed_reason: z.string() }));
const AccountV2 = z.union([
	PendingAccountFields.and(z.object({ status: z.literal("pending") })),
	OpenedAccountFields.and(z.object({ status: z.literal("open") })),
	OpenedAccountFields.and(z.object({ status: z.literal("closed") })),
	FailedAccountFields.and(z.object({ status: z.literal("failed") })),
	OpenedAccountFields.and(z.object({ status: z.literal("frozen") })),
]);
const ListPendingCustomerAccountsResponse = z.object({ results: z.array(AccountV2) });
const AccountDetails = z.object({
	identifier: AccountDetailsIdentifier,
	internal_account_id: z.string(),
});
const AddIndividualRequest = z.object({
	address: Address,
	currency_extra: z.union([z.array(IndividualCurrencyExtra), z.null()]).optional(),
	date_of_birth: z.string().regex(DOB_REGEX),
	display_name: z.string().max(140),
	first_name: z.string().max(45),
	gender: z.union([z.null(), Gender]),
	identification: z.union([z.null(), IndividualIdentification]).optional(),
	last_name: z.string().max(45),
	middle_name: z.union([z.string(), z.null()]).optional(),
	nationality: z.string().min(2).max(2),
	reference: z.string().max(36),
	relationship_type: CustomerRelationshipType.optional().default("direct"),
});
const BeneficiarySortField = z.enum([
	"display_name",
	"account_type",
	"account_number",
	"currency",
	"created_timestamp",
]);
const BeneficiarySortOrder = z.enum(["ascending", "descending"]);
const CreateAccountDetailsRequest = z.object({ primary_account_id: z.string() });
const CreateAccountDetailsResponse = z.object({ account_details_id: z.string() });
const CreatePaymentResponse = z.object({ transaction_id: z.string() });
const CreatePrimaryAccountRequest = z.object({
	account_name: z.string().max(140),
	command_id: z.string(),
	currency: Currency,
	customer_id: z.string(),
	external_id: z.string().max(36),
	internal_account_id: z.string(),
	skip_fee_check: z.union([z.boolean(), z.null()]).optional(),
});
const DomainErrorCode = z.enum([
	"user_denied_payment_authorization",
	"user_denied_approval_authorization",
	"user_denied_batch_authorization",
	"invalid_auth_state",
	"account_not_found_error",
	"remitter_account_details_not_found_error",
	"internal_account_not_found_error",
	"customer_not_found_error",
	"customer_not_approved_error",
	"individual_customer_not_allowed_to_create_aed_accounts",
	"aed_account_creation_temporarily_unavailable",
	"quote_not_found_error",
	"no_quote_for_payment_error",
	"invalid_date_range_to_before_from_error",
	"invalid_date_range_error",
	"fuse_infrastructure_error",
	"resource_not_found",
	"currencies_differ_error",
	"multiple_internal_accounts_error",
	"invalid_request_error",
	"customer_identification_incomplete",
	"invalid_transaction_reference",
	"account_operation_not_supported",
	"insufficient_balance_error",
	"account_frozen",
	"no_quote_for_account_error",
	"transaction_not_found_error",
	"unlinked_account_error",
	"not_primary_account",
	"from_and_to_account_equal",
	"viban_not_supported",
	"account_not_supported",
	"remitter_equal_to_beneficiary",
	"bank_field_restriction",
	"internal_accounts_differ_error",
	"quote_error_no_rates_set_error",
	"quote_error_missing_rate_error",
	"must_be_business",
	"bank_error",
	"invalid_amount",
	"document_not_found",
	"exchange_path_not_available",
	"market_closed",
	"public_key_already_exists",
	"public_key_already_revoked",
	"unknown",
]);
const FeatureFlag = z.object({
	created_at: z.string().regex(DOB_REGEX),
	description: z.union([z.string(), z.null()]).optional(),
	enabled: z.boolean(),
	id: z.number().int(),
	name: FeatureFlagName,
	updated_at: z.string().datetime(),
});
const FeeBasis = z.enum(["Amount", "Volume", "TransactounCount"]);
const FeeCurrency = z.enum(["aed", "usd", "stablecoin", "other"]);
const FreezeAccountRequest = z.object({ reason: z.string() });
const GetCustomerResponse = Customer;
const InternalPaymentTypeQuery = z.enum([
	"Local",
	"InternalTransfer",
	"FXTransfer",
	"LocalReturnPayment",
	"Fee",
	"Transfer",
]);
const InternationalFirstTier = z.object({ fee_type: InternationalFeeTypes, to: z.number() });
const InternationalLastTier = z.object({ fee_type: InternationalFeeTypes, from: z.number() });
const InternationalSingleTier = z.object({ fee_type: InternationalFeeTypes });
const InternationalTierType = InternationalSingleTier.and(
	z.object({ tier_type: z.literal("single") }),
);
const InternationalWriteFee = InternationalSingleTier;
const InternationalWriteFeeContainer = z.object({
	fees: z.record(InternationalWriteFee),
	minimum_fee: z.union([z.number(), z.null()]).optional(),
	vat: z.union([z.number(), z.null()]).optional(),
});
const ListBeneficiariesRequest = z
	.object({
		currency: z.union([z.null(), Currency]),
		cursor: z.union([z.string(), z.null()]),
		display_name_search: z.union([z.string(), z.null()]),
		page_size: z.union([z.number(), z.null()]),
		sort_by: z.union([z.null(), BeneficiarySortField]),
		sort_order: z.union([z.null(), BeneficiarySortOrder]),
	})
	.partial();
const ListFeatureFlagsResponse = z.object({ feature_flags: z.array(FeatureFlag) });
const ListReportsRequest = z
	.object({
		cursor: z.union([z.string(), z.null()]),
		page_size: z.union([z.number(), z.null()]),
		payload: z.union([z.null(), ResourcePayload]),
		status: z.union([z.null(), ReportStatus]),
	})
	.partial();
const ListTransactionsPagedResponse = z.object({
	pages: Cursor_String,
	results: z.array(Transaction),
});
const MakerFilter = z.enum(["all", "own", "others"]);
const MonthParam = z.object({ month: z.number().int().gte(0), year: z.number().int() });
const OnboardingCurrency = z.enum(["usd", "eur", "gbp", "aed"]);
const OnboardingProduct = z.literal("cash_management");
const OpsPlatformUserInfo = z.object({
	email: z.union([z.string(), z.null()]).optional(),
	email_verified: z.union([z.boolean(), z.null()]).optional(),
	name: z.union([z.string(), z.null()]).optional(),
	permissions: z.array(z.string()),
	picture: z.union([z.string(), z.null()]).optional(),
	scopes: z.array(z.string()),
	sub: z.string(),
});
const PatchAccountRequest = z
	.object({
		account_name: z.string().max(140),
		external_id: z.string().max(36),
	})
	.partial();
const ProvisionClientMoney = z.object({
	account_name: z.string().max(140),
	external_id: z.string(),
	internal_account_id: z.string(),
});
const ProvisionClientMoneyResponse = z.object({ account_id: z.string() });
const ProvisionSubAccount = z.object({
	account_identifier: AccountIdentifier,
	account_name: z.string().max(140),
	account_type: SubAccountType,
	currency: Currency,
	external_id: z.string(),
	internal_account_id: z.string(),
});
const ProvisionSubAccountResponse = z.object({ account_id: z.string() });
const ReadOverrideFeeType = z.union([
	z.object({
		PrimaryAccount: z.object({ direction: Direction, fee: LocalReadFee }),
	}),
	z.object({
		AccountId: z.object({
			account_id: z.string(),
			direction: Direction,
			fee: LocalReadFee,
		}),
	}),
]);
const ReportId = z.object({ id: z.string() });
const ReportRequest = z.object({ payload: ResourcePayload });
const SortOrder = z.enum(["ascending", "descending"]);
const WebhookCategory = z.enum([
	"account",
	"payment",
	"transfer",
	"exchange_transfer",
	"onboarding",
	"customer",
]);
const zand_CreatePrimaryAccountResponse = z.object({ account_id: z.string() });

export type FuseInternalPortalUserInfo = z.infer<typeof PortalUserInfo>;
export type FuseInternalWireBankDetails = z.infer<typeof WireBankDetails>;
export type FuseInternalScanDetails = z.infer<typeof ScanDetails>;
export type FuseInternalAccountDetailsIdentifier = z.infer<typeof AccountDetailsIdentifier>;
export type FuseInternalAddress = z.infer<typeof Address>;
export type FuseInternalBicCode = z.infer<typeof BicCode>;
export type FuseInternalIntermediaryBank = z.infer<typeof IntermediaryBank>;
export type FuseInternalBeneficiaryBank = z.infer<typeof BeneficiaryBank>;
export type FuseInternaluae_id = z.infer<typeof uae_id>;
export type FuseInternalpassport = z.infer<typeof passport>;
export type FuseInternalIdentification = z.infer<typeof Identification>;
export type FuseInternalCounterpartType = z.infer<typeof CounterpartType>;
export type FuseInternalPaymentDetails = z.infer<typeof PaymentDetails>;
export type FuseInternalPurposeCode = z.infer<typeof PurposeCode>;
export type FuseInternalCreateCustomerPaymentRequest = z.infer<typeof CreateCustomerPaymentRequest>;
export type FuseInternalAuthorizePaymentResponse = z.infer<typeof AuthorizePaymentResponse>;
export type FuseInternalcursor = z.infer<typeof cursor>;
export type FuseInternalPageMetadata = z.infer<typeof PageMetadata>;
export type FuseInternalResourceAction = z.infer<typeof ResourceAction>;
export type FuseInternalCurrency = z.infer<typeof Currency>;
export type FuseInternalResourcePayloadResponse = z.infer<typeof ResourcePayloadResponse>;
export type FuseInternalResourceType = z.infer<typeof ResourceType>;
export type FuseInternalApprovalRequestState = z.infer<typeof ApprovalRequestState>;
export type FuseInternalApprovalRequestResponse = z.infer<typeof ApprovalRequestResponse>;
export type FuseInternalListApprovalRequestsResponse = z.infer<typeof ListApprovalRequestsResponse>;
export type FuseInternalBatchType = z.infer<typeof BatchType>;
export type FuseInternalUploadBatchFileRequest = z.infer<typeof UploadBatchFileRequest>;
export type FuseInternalResourcePayloadRequest = z.infer<typeof ResourcePayloadRequest>;
export type FuseInternalInitiateApprovalRequestRequest = z.infer<
	typeof InitiateApprovalRequestRequest
>;
export type FuseInternalInitiateApprovalRequestResponse = z.infer<
	typeof InitiateApprovalRequestResponse
>;
export type FuseInternalApproveApprovalRequestResponse = z.infer<
	typeof ApproveApprovalRequestResponse
>;
export type FuseInternalRejectApprovalRequestRequest = z.infer<typeof RejectApprovalRequestRequest>;
export type FuseInternalEnableBankIntegrationRequest = z.infer<typeof EnableBankIntegrationRequest>;
export type FuseInternalAccountIdentifier = z.infer<typeof AccountIdentifier>;
export type FuseInternalBeneficiaryResponse = z.infer<typeof BeneficiaryResponse>;
export type FuseInternalListBeneficiariesPagedResponse = z.infer<
	typeof ListBeneficiariesPagedResponse
>;
export type FuseInternalSaveBeneficiaryRequest = z.infer<typeof SaveBeneficiaryRequest>;
export type FuseInternalpage_size = z.infer<typeof page_size>;
export type FuseInternalCashManagementTransactionType = z.infer<
	typeof CashManagementTransactionType
>;
export type FuseInternaltransaction_type = z.infer<typeof transaction_type>;
export type FuseInternalCashManagementTransactionState = z.infer<
	typeof CashManagementTransactionState
>;
export type FuseInternalstate = z.infer<typeof state>;
export type FuseInternalCashManagementTransaction = z.infer<typeof CashManagementTransaction>;
export type FuseInternalListCashManagementTransactionsResponse = z.infer<
	typeof ListCashManagementTransactionsResponse
>;
export type FuseInternalRetryCashManagementTransferResponse = z.infer<
	typeof RetryCashManagementTransferResponse
>;
export type FuseInternalCreateClientResponse = z.infer<typeof CreateClientResponse>;
export type FuseInternalErrorCode = z.infer<typeof ErrorCode>;
export type FuseInternalOnboardingError = z.infer<typeof OnboardingError>;
export type FuseInternalOnboardingStatus = z.infer<typeof OnboardingStatus>;
export type FuseInternalAedOnboarding = z.infer<typeof AedOnboarding>;
export type FuseInternalDlq = z.infer<typeof Dlq>;
export type FuseInternalGetDlqsResponse = z.infer<typeof GetDlqsResponse>;
export type FuseInternalDropDlqMessagesRequest = z.infer<typeof DropDlqMessagesRequest>;
export type FuseInternalDropDlqMessageOutcome = z.infer<typeof DropDlqMessageOutcome>;
export type FuseInternalDropDlqMessagesResponse = z.infer<typeof DropDlqMessagesResponse>;
export type FuseInternalDlqMessage = z.infer<typeof DlqMessage>;
export type FuseInternalGetDlqMessagesResponse = z.infer<typeof GetDlqMessagesResponse>;
export type FuseInternalRetryDlqMessagesRequest = z.infer<typeof RetryDlqMessagesRequest>;
export type FuseInternalRetryDlqMessageOutcome = z.infer<typeof RetryDlqMessageOutcome>;
export type FuseInternalRetryDlqMessagesResponse = z.infer<typeof RetryDlqMessagesResponse>;
export type FuseInternalFeatureFlagName = z.infer<typeof FeatureFlagName>;
export type FuseInternalUpdateFeatureFlagRequest = z.infer<typeof UpdateFeatureFlagRequest>;
export type FuseInternalNexusVolumeResponse = z.infer<typeof NexusVolumeResponse>;
export type FuseInternalOperationType = z.infer<typeof OperationType>;
export type FuseInternalStatus = z.infer<typeof Status>;
export type FuseInternalOperationSummary = z.infer<typeof OperationSummary>;
export type FuseInternalSafeguardedForex = z.infer<typeof SafeguardedForex>;
export type FuseInternalCrossBankForexState = z.infer<typeof CrossBankForexState>;
export type FuseInternalAmountInMinor = z.infer<typeof AmountInMinor>;
export type FuseInternalParty = z.infer<typeof Party>;
export type FuseInternalRate = z.infer<typeof Rate>;
export type FuseInternalCrossBankForex = z.infer<typeof CrossBankForex>;
export type FuseInternalForex = z.infer<typeof Forex>;
export type FuseInternalPassport = z.infer<typeof Passport>;
export type FuseInternalIdNumber = z.infer<typeof IdNumber>;
export type FuseInternalLocalCounterpart = z.infer<typeof LocalCounterpart>;
export type FuseInternalSafeguardedPaymentState = z.infer<typeof SafeguardedPaymentState>;
export type FuseInternalSafeguardedPayment = z.infer<typeof SafeguardedPayment>;
export type FuseInternalPayment = z.infer<typeof Payment>;
export type FuseInternalInbound = z.infer<typeof Inbound>;
export type FuseInternalReturned = z.infer<typeof Returned>;
export type FuseInternalTransfer = z.infer<typeof Transfer>;
export type FuseInternalOperation = z.infer<typeof Operation>;
export type FuseInternalupdate_OperationType = z.infer<typeof update_OperationType>;
export type FuseInternalFeeSourceAccount = z.infer<typeof FeeSourceAccount>;
export type FuseInternalOnboardingMode = z.infer<typeof OnboardingMode>;
export type FuseInternalWebhookType = z.infer<typeof WebhookType>;
export type FuseInternalWebhookEntry = z.infer<typeof WebhookEntry>;
export type FuseInternalWebhookGroup = z.infer<typeof WebhookGroup>;
export type FuseInternalOrganisationView = z.infer<typeof OrganisationView>;
export type FuseInternalFxPrice = z.infer<typeof FxPrice>;
export type FuseInternalForexEntry = z.infer<typeof ForexEntry>;
export type FuseInternalForexEntries = z.infer<typeof ForexEntries>;
export type FuseInternalFixedFee = z.infer<typeof FixedFee>;
export type FuseInternalIbanFeeTypes = z.infer<typeof IbanFeeTypes>;
export type FuseInternalIbanSingleTier = z.infer<typeof IbanSingleTier>;
export type FuseInternalIbanFirstTier = z.infer<typeof IbanFirstTier>;
export type FuseInternalIbanLastTier = z.infer<typeof IbanLastTier>;
export type FuseInternalIbanTier = z.infer<typeof IbanTier>;
export type FuseInternalIbanMultiTier = z.infer<typeof IbanMultiTier>;
export type FuseInternalIbanTierType = z.infer<typeof IbanTierType>;
export type FuseInternalIbanWriteFee = z.infer<typeof IbanWriteFee>;
export type FuseInternalMonthlyMinimum = z.infer<typeof MonthlyMinimum>;
export type FuseInternalBasisPointsWithBase = z.infer<typeof BasisPointsWithBase>;
export type FuseInternalBasisPoints = z.infer<typeof BasisPoints>;
export type FuseInternalFeeType = z.infer<typeof FeeType>;
export type FuseInternalLocalDecimalFirstTier = z.infer<typeof LocalDecimalFirstTier>;
export type FuseInternalLocalDecimalLastTier = z.infer<typeof LocalDecimalLastTier>;
export type FuseInternalLocalDecimalTier = z.infer<typeof LocalDecimalTier>;
export type FuseInternalLocalWriteFeeDecimal = z.infer<typeof LocalWriteFeeDecimal>;
export type FuseInternalLocalIntegerFirstTier = z.infer<typeof LocalIntegerFirstTier>;
export type FuseInternalLocalIntegerLastTier = z.infer<typeof LocalIntegerLastTier>;
export type FuseInternalLocalIntegerTier = z.infer<typeof LocalIntegerTier>;
export type FuseInternalLocalWriteFeeInteger = z.infer<typeof LocalWriteFeeInteger>;
export type FuseInternalLocalWriteFee = z.infer<typeof LocalWriteFee>;
export type FuseInternalWriteFeeSpecCondition = z.infer<typeof WriteFeeSpecCondition>;
export type FuseInternalWriteFeeSpec = z.infer<typeof WriteFeeSpec>;
export type FuseInternalStoreFeeRequest = z.infer<typeof StoreFeeRequest>;
export type FuseInternalCreateOrganisationRequest = z.infer<typeof CreateOrganisationRequest>;
export type FuseInternalCreateOrganisationResponse = z.infer<typeof CreateOrganisationResponse>;
export type FuseInternalUpdateOrganisationRequest = z.infer<typeof UpdateOrganisationRequest>;
export type FuseInternalIbanReadFee = z.infer<typeof IbanReadFee>;
export type FuseInternalInternationalFeeTypes = z.infer<typeof InternationalFeeTypes>;
export type FuseInternalInternationalTier = z.infer<typeof InternationalTier>;
export type FuseInternalInternationalReadFee = z.infer<typeof InternationalReadFee>;
export type FuseInternalLocalReadFeeDecimal = z.infer<typeof LocalReadFeeDecimal>;
export type FuseInternalLocalReadFeeInteger = z.infer<typeof LocalReadFeeInteger>;
export type FuseInternalLocalReadFee = z.infer<typeof LocalReadFee>;
export type FuseInternalReadOrgFeeResponse = z.infer<typeof ReadOrgFeeResponse>;
export type FuseInternalOrganisationFees = z.infer<typeof OrganisationFees>;
export type FuseInternalAllOrganisationsFeesResponse = z.infer<typeof AllOrganisationsFeesResponse>;
export type FuseInternalGetOrganisationConfigResponse = z.infer<
	typeof GetOrganisationConfigResponse
>;
export type FuseInternalWebhookConfigRequest = z.infer<typeof WebhookConfigRequest>;
export type FuseInternalCreateAccountType = z.infer<typeof CreateAccountType>;
export type FuseInternalEnabledForCurrency = z.infer<typeof EnabledForCurrency>;
export type FuseInternalEnabledCurrenciesResponse = z.infer<typeof EnabledCurrenciesResponse>;
export type FuseInternalPublicKeyResponse = z.infer<typeof PublicKeyResponse>;
export type FuseInternalRegisterPublicKeyRequest = z.infer<typeof RegisterPublicKeyRequest>;
export type FuseInternalUser = z.infer<typeof User>;
export type FuseInternalInvitedUser = z.infer<typeof InvitedUser>;
export type FuseInternalListUsersForOrganisationResponse = z.infer<
	typeof ListUsersForOrganisationResponse
>;
export type FuseInternalInviteUserRequest = z.infer<typeof InviteUserRequest>;
export type FuseInternalRegisterInvitedUserRequest = z.infer<typeof RegisterInvitedUserRequest>;
export type FuseInternalAssignRoleToUserRequest = z.infer<typeof AssignRoleToUserRequest>;
export type FuseInternalRole = z.infer<typeof Role>;
export type FuseInternalUserRolesRequest = z.infer<typeof UserRolesRequest>;
export type FuseInternalUpdateWebhooksRequest = z.infer<typeof UpdateWebhooksRequest>;
export type FuseInternalAccountType = z.infer<typeof AccountType>;
export type FuseInternalPaymentScheme = z.infer<typeof PaymentScheme>;
export type FuseInternalAddressableDetails = z.infer<typeof AddressableDetails>;
export type FuseInternalAccountStatus = z.infer<typeof AccountStatus>;
export type FuseInternalAccount = z.infer<typeof Account>;
export type FuseInternalArchiveOrganisationResponse = z.infer<typeof ArchiveOrganisationResponse>;
export type FuseInternalCreateCashManagementAccountRequest = z.infer<
	typeof CreateCashManagementAccountRequest
>;
export type FuseInternalCreateCashManagementAccountResponse = z.infer<
	typeof CreateCashManagementAccountResponse
>;
export type FuseInternalAprTierResponse = z.infer<typeof AprTierResponse>;
export type FuseInternalApprovalStatus = z.infer<typeof ApprovalStatus>;
export type FuseInternalCustomerTypeFilter = z.infer<typeof CustomerTypeFilter>;
export type FuseInternalCustomerRelationshipType = z.infer<typeof CustomerRelationshipType>;
export type FuseInternalCustomerSummary = z.infer<typeof CustomerSummary>;
export type FuseInternalCursor_String = z.infer<typeof Cursor_String>;
export type FuseInternalListCustomersResponse = z.infer<typeof ListCustomersResponse>;
export type FuseInternalIdDocumentType = z.infer<typeof IdDocumentType>;
export type FuseInternalIndividualCurrencyExtra = z.infer<typeof IndividualCurrencyExtra>;
export type FuseInternalGender = z.infer<typeof Gender>;
export type FuseInternalIndividualIdentification = z.infer<typeof IndividualIdentification>;
export type FuseInternalAedLicenseIssuer = z.infer<typeof AedLicenseIssuer>;
export type FuseInternalBusinessCurrencyExtra = z.infer<typeof BusinessCurrencyExtra>;
export type FuseInternalRegulatoryLicenseType = z.infer<typeof RegulatoryLicenseType>;
export type FuseInternalCustomerExtraAPI = z.infer<typeof CustomerExtraAPI>;
export type FuseInternalDocumentType = z.infer<typeof DocumentType>;
export type FuseInternalDocumentSummary = z.infer<typeof DocumentSummary>;
export type FuseInternalRejectionReason = z.infer<typeof RejectionReason>;
export type FuseInternalCustomer = z.infer<typeof Customer>;
export type FuseInternalComplianceProfile = z.infer<typeof ComplianceProfile>;
export type FuseInternalAgreementAcceptance = z.infer<typeof AgreementAcceptance>;
export type FuseInternalIdentityCheck = z.infer<typeof IdentityCheck>;
export type FuseInternalVerificationResult = z.infer<typeof VerificationResult>;
export type FuseInternalPhotoCheck = z.infer<typeof PhotoCheck>;
export type FuseInternalDocumentCheck = z.infer<typeof DocumentCheck>;
export type FuseInternalWatchlistRecord = z.infer<typeof WatchlistRecord>;
export type FuseInternalWatchlistCheck = z.infer<typeof WatchlistCheck>;
export type FuseInternalIdentificationResult = z.infer<typeof IdentificationResult>;
export type FuseInternalIdentificationsCheck = z.infer<typeof IdentificationsCheck>;
export type FuseInternalComplianceCheckDetails = z.infer<typeof ComplianceCheckDetails>;
export type FuseInternalComplianceCheck = z.infer<typeof ComplianceCheck>;
export type FuseInternalRiskLevel = z.infer<typeof RiskLevel>;
export type FuseInternalComplianceVerifications = z.infer<typeof ComplianceVerifications>;
export type FuseInternalDisclosures = z.infer<typeof Disclosures>;
export type FuseInternalFinancialProfile = z.infer<typeof FinancialProfile>;
export type FuseInternalFundingSource = z.infer<typeof FundingSource>;
export type FuseInternalIndustry = z.infer<typeof Industry>;
export type FuseInternalLegalEntityType = z.infer<typeof LegalEntityType>;
export type FuseInternalTaxIdType = z.infer<typeof TaxIdType>;
export type FuseInternalTaxId = z.infer<typeof TaxId>;
export type FuseInternalAlpacaOnboardingData = z.infer<typeof AlpacaOnboardingData>;
export type FuseInternalDocumentsStatus = z.infer<typeof DocumentsStatus>;
export type FuseInternalGetCustomerInternalResponse = z.infer<typeof GetCustomerInternalResponse>;
export type FuseInternalUpdateCustomerApprovalStatusRequest = z.infer<
	typeof UpdateCustomerApprovalStatusRequest
>;
export type FuseInternalProduct = z.infer<typeof Product>;
export type FuseInternalManuallyCompleteOnboarding = z.infer<typeof ManuallyCompleteOnboarding>;
export type FuseInternalErrorResponse = z.infer<typeof ErrorResponse>;
export type FuseInternalLinkDocumentRequest = z.infer<typeof LinkDocumentRequest>;
export type FuseInternalProductOnboarding = z.infer<typeof ProductOnboarding>;
export type FuseInternalOnboardToProduct = z.infer<typeof OnboardToProduct>;
export type FuseInternalOverwriteOnboarding = z.infer<typeof OverwriteOnboarding>;
export type FuseInternalReflectOnboarding = z.infer<typeof ReflectOnboarding>;
export type FuseInternalRetryOnboarding = z.infer<typeof RetryOnboarding>;
export type FuseInternalUpdateShareholderRequest = z.infer<typeof UpdateShareholderRequest>;
export type FuseInternalUploadDocumentRequest = z.infer<typeof UploadDocumentRequest>;
export type FuseInternalUploadDocumentResponse = z.infer<typeof UploadDocumentResponse>;
export type FuseInternalDocument = z.infer<typeof Document>;
export type FuseInternalDirection = z.infer<typeof Direction>;
export type FuseInternalWriteOverrideFeeType = z.infer<typeof WriteOverrideFeeType>;
export type FuseInternalUpdateFeeSpec = z.infer<typeof UpdateFeeSpec>;
export type FuseInternalPatchFeeRequest = z.infer<typeof PatchFeeRequest>;
export type FuseInternalCreateInternalTransferToPrimaryAccountRequest = z.infer<
	typeof CreateInternalTransferToPrimaryAccountRequest
>;
export type FuseInternalCreateInternalTransferToPrimaryAccountResponse = z.infer<
	typeof CreateInternalTransferToPrimaryAccountResponse
>;
export type FuseInternalOnboardOrganisationRequest = z.infer<typeof OnboardOrganisationRequest>;
export type FuseInternalOnboardOrganisationResponse = z.infer<typeof OnboardOrganisationResponse>;
export type FuseInternalOrganisationOnboardingEntry = z.infer<typeof OrganisationOnboardingEntry>;
export type FuseInternalOrganisationOnboardingsResponse = z.infer<
	typeof OrganisationOnboardingsResponse
>;
export type FuseInternalCreateAccountRequest = z.infer<typeof CreateAccountRequest>;
export type FuseInternalMonth = z.infer<typeof Month>;
export type FuseInternalMonthlyStatementStatus = z.infer<typeof MonthlyStatementStatus>;
export type FuseInternalMonthlyStatementProgress = z.infer<typeof MonthlyStatementProgress>;
export type FuseInternalGetMonthlyStatementsResponse = z.infer<typeof GetMonthlyStatementsResponse>;
export type FuseInternalRequestMonthlyStatementsRequest = z.infer<
	typeof RequestMonthlyStatementsRequest
>;
export type FuseInternalRequestedMonth = z.infer<typeof RequestedMonth>;
export type FuseInternalRequestMonthlyStatementsResponse = z.infer<
	typeof RequestMonthlyStatementsResponse
>;
export type FuseInternalSubAccountType = z.infer<typeof SubAccountType>;
export type FuseInternalReflectSystemAccountAsCashManagementRequest = z.infer<
	typeof ReflectSystemAccountAsCashManagementRequest
>;
export type FuseInternalReflectSystemAccountAsCashManagementResponse = z.infer<
	typeof ReflectSystemAccountAsCashManagementResponse
>;
export type FuseInternalReflectReturnedPaymentRequest = z.infer<
	typeof ReflectReturnedPaymentRequest
>;
export type FuseInternalReflectReturnedPaymentResponse = z.infer<
	typeof ReflectReturnedPaymentResponse
>;
export type FuseInternalReflectExecutedRequest = z.infer<typeof ReflectExecutedRequest>;
export type FuseInternalReflectFailedRequest = z.infer<typeof ReflectFailedRequest>;
export type FuseInternalReflectSubmittedRequest = z.infer<typeof ReflectSubmittedRequest>;
export type FuseInternalUnonboardOrganisationRequest = z.infer<typeof UnonboardOrganisationRequest>;
export type FuseInternalTransactionsStatementPayload = z.infer<typeof TransactionsStatementPayload>;
export type FuseInternalMonthlyStatementPayload = z.infer<typeof MonthlyStatementPayload>;
export type FuseInternalpayload = z.infer<typeof payload>;
export type FuseInternalResourcePayload = z.infer<typeof ResourcePayload>;
export type FuseInternalReportStatus = z.infer<typeof ReportStatus>;
export type FuseInternalReportResponse = z.infer<typeof ReportResponse>;
export type FuseInternalListReportsPagedResponse = z.infer<typeof ListReportsPagedResponse>;
export type FuseInternalRetryPendingSubAccountsRequest = z.infer<
	typeof RetryPendingSubAccountsRequest
>;
export type FuseInternalRetrySubAccountOutcome = z.infer<typeof RetrySubAccountOutcome>;
export type FuseInternalRetryPendingSubAccountsResponse = z.infer<
	typeof RetryPendingSubAccountsResponse
>;
export type FuseInternalPermission = z.infer<typeof Permission>;
export type FuseInternalSelectedOptionRequest = z.infer<typeof SelectedOptionRequest>;
export type FuseInternalAnswerValueRequest = z.infer<typeof AnswerValueRequest>;
export type FuseInternalSubmittedAnswerRequest = z.infer<typeof SubmittedAnswerRequest>;
export type FuseInternalRequestAdvisoryRequest = z.infer<typeof RequestAdvisoryRequest>;
export type FuseInternalSleeveResponse = z.infer<typeof SleeveResponse>;
export type FuseInternalSleeveTargetResponse = z.infer<typeof SleeveTargetResponse>;
export type FuseInternalRungResponse = z.infer<typeof RungResponse>;
export type FuseInternalLadderResponse = z.infer<typeof LadderResponse>;
export type FuseInternalRecommendationResponse = z.infer<typeof RecommendationResponse>;
export type FuseInternalAdvisoryResponse = z.infer<typeof AdvisoryResponse>;
export type FuseInternalAdvisoryStatusRequest = z.infer<typeof AdvisoryStatusRequest>;
export type FuseInternalAdvisoryDecisionRequest = z.infer<typeof AdvisoryDecisionRequest>;
export type FuseInternalAdvisoryStatusResponse = z.infer<typeof AdvisoryStatusResponse>;
export type FuseInternalAdvisoryDecisionResponse = z.infer<typeof AdvisoryDecisionResponse>;
export type FuseInternalDetailSpecResponse = z.infer<typeof DetailSpecResponse>;
export type FuseInternalAnswerOptionResponse = z.infer<typeof AnswerOptionResponse>;
export type FuseInternalAnswerSpecResponse = z.infer<typeof AnswerSpecResponse>;
export type FuseInternalQuestionResponse = z.infer<typeof QuestionResponse>;
export type FuseInternalQuestionnaireResponse = z.infer<typeof QuestionnaireResponse>;
export type FuseInternaltransaction_Batch = z.infer<typeof transaction_Batch>;
export type FuseInternalBeneficiaryDetails = z.infer<typeof BeneficiaryDetails>;
export type FuseInternalBeneficiaryExtraDetails = z.infer<typeof BeneficiaryExtraDetails>;
export type FuseInternalPaymentFailure = z.infer<typeof PaymentFailure>;
export type FuseInternalFeeSourceRef = z.infer<typeof FeeSourceRef>;
export type FuseInternalRemitterDetails = z.infer<typeof RemitterDetails>;
export type FuseInternalTransactionState = z.infer<typeof TransactionState>;
export type FuseInternalLocalPayment = z.infer<typeof LocalPayment>;
export type FuseInternalLocalReturnPayment = z.infer<typeof LocalReturnPayment>;
export type FuseInternalInternalTransferTransactionState = z.infer<
	typeof InternalTransferTransactionState
>;
export type FuseInternalInternalTransfer = z.infer<typeof InternalTransfer>;
export type FuseInternalFxTransferTransactionState = z.infer<typeof FxTransferTransactionState>;
export type FuseInternalFxTransfer = z.infer<typeof FxTransfer>;
export type FuseInternalFeeTargetRef = z.infer<typeof FeeTargetRef>;
export type FuseInternalFeeDebit = z.infer<typeof FeeDebit>;
export type FuseInternalTransaction = z.infer<typeof Transaction>;
export type FuseInternalListAllTransactionsPagedResponse = z.infer<
	typeof ListAllTransactionsPagedResponse
>;
export type FuseInternalAuthorizeBatchPaymentResponse = z.infer<
	typeof AuthorizeBatchPaymentResponse
>;
export type FuseInternalBatchDryRunSummary = z.infer<typeof BatchDryRunSummary>;
export type FuseInternalTierBasis = z.infer<typeof TierBasis>;
export type FuseInternalFeeThreshold = z.infer<typeof FeeThreshold>;
export type FuseInternalVariableFee = z.infer<typeof VariableFee>;
export type FuseInternalVat = z.infer<typeof Vat>;
export type FuseInternalFeeCalculation = z.infer<typeof FeeCalculation>;
export type FuseInternalRetryTransferResponse = z.infer<typeof RetryTransferResponse>;
export type FuseInternalRegisterUserRequest = z.infer<typeof RegisterUserRequest>;
export type FuseInternalRegisterUserResponse = z.infer<typeof RegisterUserResponse>;
export type FuseInternalOrganisation = z.infer<typeof Organisation>;
export type FuseInternalAccountTypeV2 = z.infer<typeof AccountTypeV2>;
export type FuseInternalSharedPendingReason = z.infer<typeof SharedPendingReason>;
export type FuseInternalZenusPendingReason = z.infer<typeof ZenusPendingReason>;
export type FuseInternalPendingReason = z.infer<typeof PendingReason>;
export type FuseInternalPendingAccount = z.infer<typeof PendingAccount>;
export type FuseInternalListPendingAccountsPagedResponse = z.infer<
	typeof ListPendingAccountsPagedResponse
>;
export type FuseInternalCreateAccountRequestV2 = z.infer<typeof CreateAccountRequestV2>;
export type FuseInternalCreateAccountResponse = z.infer<typeof CreateAccountResponse>;
export type FuseInternalReadFeeSpecCondition = z.infer<typeof ReadFeeSpecCondition>;
export type FuseInternalReadFeeSpecV4 = z.infer<typeof ReadFeeSpecV4>;
export type FuseInternalReadOrgFeeResponseV4 = z.infer<typeof ReadOrgFeeResponseV4>;
export type FuseInternalOrganisationFeesV2 = z.infer<typeof OrganisationFeesV2>;
export type FuseInternalAllOrganisationsFeesResponseV2 = z.infer<
	typeof AllOrganisationsFeesResponseV2
>;
export type FuseInternalReadOrgFeeResponseV2 = z.infer<typeof ReadOrgFeeResponseV2>;
export type FuseInternalReadFeeSpecV3 = z.infer<typeof ReadFeeSpecV3>;
export type FuseInternalReadOrgFeeResponseV3 = z.infer<typeof ReadOrgFeeResponseV3>;
export type FuseInternalSearchAccountsPagedResponse = z.infer<typeof SearchAccountsPagedResponse>;
export type FuseInternalEstimateFeeForBeneficiaryRequest = z.infer<
	typeof EstimateFeeForBeneficiaryRequest
>;
export type FuseInternalEstimateFeeForBeneficiaryResponse = z.infer<
	typeof EstimateFeeForBeneficiaryResponse
>;
export type FuseInternalCashManagementInterestState = z.infer<typeof CashManagementInterestState>;
export type FuseInternalCurrencyInterest = z.infer<typeof CurrencyInterest>;
export type FuseInternalCashManagementInterest = z.infer<typeof CashManagementInterest>;
export type FuseInternalGetCashManagementAccountResponse = z.infer<
	typeof GetCashManagementAccountResponse
>;
export type FuseInternalCashManagementCashLimitsResponse = z.infer<
	typeof CashManagementCashLimitsResponse
>;
export type FuseInternalCashInterestStatusRequest = z.infer<typeof CashInterestStatusRequest>;
export type FuseInternalCashInterestProgramRequest = z.infer<typeof CashInterestProgramRequest>;
export type FuseInternalRequestCashInterestChangeRequest = z.infer<
	typeof RequestCashInterestChangeRequest
>;
export type FuseInternalRequestCashInterestChangeResponse = z.infer<
	typeof RequestCashInterestChangeResponse
>;
export type FuseInternalFundIdentifier = z.infer<typeof FundIdentifier>;
export type FuseInternalInvestmentAmount = z.infer<typeof InvestmentAmount>;
export type FuseInternalCreateBuyOrderRequest = z.infer<typeof CreateBuyOrderRequest>;
export type FuseInternalCreateBuyOrderResponse = z.infer<typeof CreateBuyOrderResponse>;
export type FuseInternalGetMarketClockResponse = z.infer<typeof GetMarketClockResponse>;
export type FuseInternalCreateSellOrderRequest = z.infer<typeof CreateSellOrderRequest>;
export type FuseInternalCreateSellOrderResponse = z.infer<typeof CreateSellOrderResponse>;
export type FuseInternalCashManagementAssetClass = z.infer<typeof CashManagementAssetClass>;
export type FuseInternalasset_class = z.infer<typeof asset_class>;
export type FuseInternalCashManagementBondType = z.infer<typeof CashManagementBondType>;
export type FuseInternalbond_type = z.infer<typeof bond_type>;
export type FuseInternalCashManagementBondStatus = z.infer<typeof CashManagementBondStatus>;
export type FuseInternalCashManagementCorporateDetails = z.infer<
	typeof CashManagementCorporateDetails
>;
export type FuseInternalCashManagementCouponFrequency = z.infer<
	typeof CashManagementCouponFrequency
>;
export type FuseInternalCashManagementCouponType = z.infer<typeof CashManagementCouponType>;
export type FuseInternalCashManagementAssetSource = z.infer<typeof CashManagementAssetSource>;
export type FuseInternalCashManagementAsset = z.infer<typeof CashManagementAsset>;
export type FuseInternalListCashManagementAssetsResponse = z.infer<
	typeof ListCashManagementAssetsResponse
>;
export type FuseInternalCashManagementPositionStatus = z.infer<typeof CashManagementPositionStatus>;
export type FuseInternalstatus = z.infer<typeof status>;
export type FuseInternalCashManagementPosition = z.infer<typeof CashManagementPosition>;
export type FuseInternalListCashManagementPositionsResponse = z.infer<
	typeof ListCashManagementPositionsResponse
>;
export type FuseInternalTransferAccountType = z.infer<typeof TransferAccountType>;
export type FuseInternalCreateTransferRequest = z.infer<typeof CreateTransferRequest>;
export type FuseInternalCreateTransferResponse = z.infer<typeof CreateTransferResponse>;
export type FuseInternalAddBusinessWithDocsRequest = z.infer<typeof AddBusinessWithDocsRequest>;
export type FuseInternalAddCustomerResponse = z.infer<typeof AddCustomerResponse>;
export type FuseInternalCustomerDryRunRequest = z.infer<typeof CustomerDryRunRequest>;
export type FuseInternalCustomerValidationDetails = z.infer<typeof CustomerValidationDetails>;
export type FuseInternalCustomerValidationField = z.infer<typeof CustomerValidationField>;
export type FuseInternalCustomerValidationError = z.infer<typeof CustomerValidationError>;
export type FuseInternalCustomerDryRunResponse = z.infer<typeof CustomerDryRunResponse>;
export type FuseInternalAddIndividualWithDocsRequest = z.infer<typeof AddIndividualWithDocsRequest>;
export type FuseInternalSearchCustomersPagedResponse = z.infer<typeof SearchCustomersPagedResponse>;
export type FuseInternalOnboardingRequiredDocumentsStatus = z.infer<
	typeof OnboardingRequiredDocumentsStatus
>;
export type FuseInternalInternalAccountType = z.infer<typeof InternalAccountType>;
export type FuseInternalCreateInternalAccountRequest = z.infer<typeof CreateInternalAccountRequest>;
export type FuseInternalCreateInternalAccountResponse = z.infer<
	typeof CreateInternalAccountResponse
>;
export type FuseInternalPortalBatch = z.infer<typeof PortalBatch>;
export type FuseInternalPortalFeeSourceRef = z.infer<typeof PortalFeeSourceRef>;
export type FuseInternalPortalRemitterDetails = z.infer<typeof PortalRemitterDetails>;
export type FuseInternalPortalLocalPayment = z.infer<typeof PortalLocalPayment>;
export type FuseInternalPortalLocalReturnPayment = z.infer<typeof PortalLocalReturnPayment>;
export type FuseInternalPortalInternalTransfer = z.infer<typeof PortalInternalTransfer>;
export type FuseInternalPortalFxTransfer = z.infer<typeof PortalFxTransfer>;
export type FuseInternalPortalFeeDebit = z.infer<typeof PortalFeeDebit>;
export type FuseInternalPortalTransaction = z.infer<typeof PortalTransaction>;
export type FuseInternalSearchPortalTransactionsPagedResponse = z.infer<
	typeof SearchPortalTransactionsPagedResponse
>;
export type FuseInternalAccountFields = z.infer<typeof AccountFields>;
export type FuseInternalPendingAccountFields = z.infer<typeof PendingAccountFields>;
export type FuseInternalAccountExtraFields = z.infer<typeof AccountExtraFields>;
export type FuseInternalOpenedAccountFields = z.infer<typeof OpenedAccountFields>;
export type FuseInternalFailedAccountFields = z.infer<typeof FailedAccountFields>;
export type FuseInternalAccountV2 = z.infer<typeof AccountV2>;
export type FuseInternalListPendingCustomerAccountsResponse = z.infer<
	typeof ListPendingCustomerAccountsResponse
>;
export type FuseInternalAccountDetails = z.infer<typeof AccountDetails>;
export type FuseInternalAddIndividualRequest = z.infer<typeof AddIndividualRequest>;
export type FuseInternalBeneficiarySortField = z.infer<typeof BeneficiarySortField>;
export type FuseInternalBeneficiarySortOrder = z.infer<typeof BeneficiarySortOrder>;
export type FuseInternalCreateAccountDetailsRequest = z.infer<typeof CreateAccountDetailsRequest>;
export type FuseInternalCreateAccountDetailsResponse = z.infer<typeof CreateAccountDetailsResponse>;
export type FuseInternalCreatePaymentResponse = z.infer<typeof CreatePaymentResponse>;
export type FuseInternalCreatePrimaryAccountRequest = z.infer<typeof CreatePrimaryAccountRequest>;
export type FuseInternalDomainErrorCode = z.infer<typeof DomainErrorCode>;
export type FuseInternalFeatureFlag = z.infer<typeof FeatureFlag>;
export type FuseInternalFeeBasis = z.infer<typeof FeeBasis>;
export type FuseInternalFeeCurrency = z.infer<typeof FeeCurrency>;
export type FuseInternalFreezeAccountRequest = z.infer<typeof FreezeAccountRequest>;
export type FuseInternalGetCustomerResponse = z.infer<typeof GetCustomerResponse>;
export type FuseInternalInternalPaymentTypeQuery = z.infer<typeof InternalPaymentTypeQuery>;
export type FuseInternalInternationalFirstTier = z.infer<typeof InternationalFirstTier>;
export type FuseInternalInternationalLastTier = z.infer<typeof InternationalLastTier>;
export type FuseInternalInternationalSingleTier = z.infer<typeof InternationalSingleTier>;
export type FuseInternalInternationalTierType = z.infer<typeof InternationalTierType>;
export type FuseInternalInternationalWriteFee = z.infer<typeof InternationalWriteFee>;
export type FuseInternalInternationalWriteFeeContainer = z.infer<
	typeof InternationalWriteFeeContainer
>;
export type FuseInternalListBeneficiariesRequest = z.infer<typeof ListBeneficiariesRequest>;
export type FuseInternalListFeatureFlagsResponse = z.infer<typeof ListFeatureFlagsResponse>;
export type FuseInternalListReportsRequest = z.infer<typeof ListReportsRequest>;
export type FuseInternalListTransactionsPagedResponse = z.infer<
	typeof ListTransactionsPagedResponse
>;
export type FuseInternalMakerFilter = z.infer<typeof MakerFilter>;
export type FuseInternalMonthParam = z.infer<typeof MonthParam>;
export type FuseInternalOnboardingCurrency = z.infer<typeof OnboardingCurrency>;
export type FuseInternalOnboardingProduct = z.infer<typeof OnboardingProduct>;
export type FuseInternalOpsPlatformUserInfo = z.infer<typeof OpsPlatformUserInfo>;
export type FuseInternalPatchAccountRequest = z.infer<typeof PatchAccountRequest>;
export type FuseInternalProvisionClientMoney = z.infer<typeof ProvisionClientMoney>;
export type FuseInternalProvisionClientMoneyResponse = z.infer<typeof ProvisionClientMoneyResponse>;
export type FuseInternalProvisionSubAccount = z.infer<typeof ProvisionSubAccount>;
export type FuseInternalProvisionSubAccountResponse = z.infer<typeof ProvisionSubAccountResponse>;
export type FuseInternalReadOverrideFeeType = z.infer<typeof ReadOverrideFeeType>;
export type FuseInternalReportId = z.infer<typeof ReportId>;
export type FuseInternalReportRequest = z.infer<typeof ReportRequest>;
export type FuseInternalSortOrder = z.infer<typeof SortOrder>;
export type FuseInternalWebhookCategory = z.infer<typeof WebhookCategory>;
export type FuseInternalzand_CreatePrimaryAccountResponse = z.infer<
	typeof zand_CreatePrimaryAccountResponse
>;

export const schemas = {
	Account,
	AccountDetails,
	AccountDetailsIdentifier,
	AccountExtraFields,
	AccountFields,
	AccountIdentifier,
	AccountStatus,
	AccountType,
	AccountTypeV2,
	AccountV2,
	AddBusinessWithDocsRequest,
	AddCustomerResponse,
	AddIndividualRequest,
	AddIndividualWithDocsRequest,
	Address,
	AddressableDetails,
	AdvisoryDecisionRequest,
	AdvisoryDecisionResponse,
	AdvisoryResponse,
	AdvisoryStatusRequest,
	AdvisoryStatusResponse,
	AedLicenseIssuer,
	AedOnboarding,
	AgreementAcceptance,
	AllOrganisationsFeesResponse,
	AllOrganisationsFeesResponseV2,
	AlpacaOnboardingData,
	AmountInMinor,
	AnswerOptionResponse,
	AnswerSpecResponse,
	AnswerValueRequest,
	ApprovalRequestResponse,
	ApprovalRequestState,
	ApprovalStatus,
	ApproveApprovalRequestResponse,
	AprTierResponse,
	ArchiveOrganisationResponse,
	asset_class,
	AssignRoleToUserRequest,
	AuthorizeBatchPaymentResponse,
	AuthorizePaymentResponse,
	BasisPoints,
	BasisPointsWithBase,
	BatchDryRunSummary,
	BatchType,
	BeneficiaryBank,
	BeneficiaryDetails,
	BeneficiaryExtraDetails,
	BeneficiaryResponse,
	BeneficiarySortField,
	BeneficiarySortOrder,
	BicCode,
	bond_type,
	BusinessCurrencyExtra,
	CashInterestProgramRequest,
	CashInterestStatusRequest,
	CashManagementAsset,
	CashManagementAssetClass,
	CashManagementAssetSource,
	CashManagementBondStatus,
	CashManagementBondType,
	CashManagementCashLimitsResponse,
	CashManagementCorporateDetails,
	CashManagementCouponFrequency,
	CashManagementCouponType,
	CashManagementInterest,
	CashManagementInterestState,
	CashManagementPosition,
	CashManagementPositionStatus,
	CashManagementTransaction,
	CashManagementTransactionState,
	CashManagementTransactionType,
	ComplianceCheck,
	ComplianceCheckDetails,
	ComplianceProfile,
	ComplianceVerifications,
	CounterpartType,
	CreateAccountDetailsRequest,
	CreateAccountDetailsResponse,
	CreateAccountRequest,
	CreateAccountRequestV2,
	CreateAccountResponse,
	CreateAccountType,
	CreateBuyOrderRequest,
	CreateBuyOrderResponse,
	CreateCashManagementAccountRequest,
	CreateCashManagementAccountResponse,
	CreateClientResponse,
	CreateCustomerPaymentRequest,
	CreateInternalAccountRequest,
	CreateInternalAccountResponse,
	CreateInternalTransferToPrimaryAccountRequest,
	CreateInternalTransferToPrimaryAccountResponse,
	CreateOrganisationRequest,
	CreateOrganisationResponse,
	CreatePaymentResponse,
	CreatePrimaryAccountRequest,
	CreateSellOrderRequest,
	CreateSellOrderResponse,
	CreateTransferRequest,
	CreateTransferResponse,
	CrossBankForex,
	CrossBankForexState,
	Currency,
	CurrencyInterest,
	cursor,
	Cursor_String,
	Customer,
	CustomerDryRunRequest,
	CustomerDryRunResponse,
	CustomerExtraAPI,
	CustomerRelationshipType,
	CustomerSummary,
	CustomerTypeFilter,
	CustomerValidationDetails,
	CustomerValidationError,
	CustomerValidationField,
	DetailSpecResponse,
	Direction,
	Disclosures,
	Dlq,
	DlqMessage,
	Document,
	DocumentCheck,
	DocumentsStatus,
	DocumentSummary,
	DocumentType,
	DomainErrorCode,
	DropDlqMessageOutcome,
	DropDlqMessagesRequest,
	DropDlqMessagesResponse,
	EnableBankIntegrationRequest,
	EnabledCurrenciesResponse,
	EnabledForCurrency,
	ErrorCode,
	ErrorResponse,
	EstimateFeeForBeneficiaryRequest,
	EstimateFeeForBeneficiaryResponse,
	FailedAccountFields,
	FeatureFlag,
	FeatureFlagName,
	FeeBasis,
	FeeCalculation,
	FeeCurrency,
	FeeDebit,
	FeeSourceAccount,
	FeeSourceRef,
	FeeTargetRef,
	FeeThreshold,
	FeeType,
	FinancialProfile,
	FixedFee,
	Forex,
	ForexEntries,
	ForexEntry,
	FreezeAccountRequest,
	FundIdentifier,
	FundingSource,
	FxPrice,
	FxTransfer,
	FxTransferTransactionState,
	Gender,
	GetCashManagementAccountResponse,
	GetCustomerInternalResponse,
	GetCustomerResponse,
	GetDlqMessagesResponse,
	GetDlqsResponse,
	GetMarketClockResponse,
	GetMonthlyStatementsResponse,
	GetOrganisationConfigResponse,
	IbanFeeTypes,
	IbanFirstTier,
	IbanLastTier,
	IbanMultiTier,
	IbanReadFee,
	IbanSingleTier,
	IbanTier,
	IbanTierType,
	IbanWriteFee,
	IdDocumentType,
	Identification,
	IdentificationResult,
	IdentificationsCheck,
	IdentityCheck,
	IdNumber,
	Inbound,
	IndividualCurrencyExtra,
	IndividualIdentification,
	Industry,
	InitiateApprovalRequestRequest,
	InitiateApprovalRequestResponse,
	IntermediaryBank,
	InternalAccountType,
	InternalPaymentTypeQuery,
	InternalTransfer,
	InternalTransferTransactionState,
	InternationalFeeTypes,
	InternationalFirstTier,
	InternationalLastTier,
	InternationalReadFee,
	InternationalSingleTier,
	InternationalTier,
	InternationalTierType,
	InternationalWriteFee,
	InternationalWriteFeeContainer,
	InvestmentAmount,
	InvitedUser,
	InviteUserRequest,
	LadderResponse,
	LegalEntityType,
	LinkDocumentRequest,
	ListAllTransactionsPagedResponse,
	ListApprovalRequestsResponse,
	ListBeneficiariesPagedResponse,
	ListBeneficiariesRequest,
	ListCashManagementAssetsResponse,
	ListCashManagementPositionsResponse,
	ListCashManagementTransactionsResponse,
	ListCustomersResponse,
	ListFeatureFlagsResponse,
	ListPendingAccountsPagedResponse,
	ListPendingCustomerAccountsResponse,
	ListReportsPagedResponse,
	ListReportsRequest,
	ListTransactionsPagedResponse,
	ListUsersForOrganisationResponse,
	LocalCounterpart,
	LocalDecimalFirstTier,
	LocalDecimalLastTier,
	LocalDecimalTier,
	LocalIntegerFirstTier,
	LocalIntegerLastTier,
	LocalIntegerTier,
	LocalPayment,
	LocalReadFee,
	LocalReadFeeDecimal,
	LocalReadFeeInteger,
	LocalReturnPayment,
	LocalWriteFee,
	LocalWriteFeeDecimal,
	LocalWriteFeeInteger,
	MakerFilter,
	ManuallyCompleteOnboarding,
	Month,
	MonthlyMinimum,
	MonthlyStatementPayload,
	MonthlyStatementProgress,
	MonthlyStatementStatus,
	MonthParam,
	NexusVolumeResponse,
	OnboardingCurrency,
	OnboardingError,
	OnboardingMode,
	OnboardingProduct,
	OnboardingRequiredDocumentsStatus,
	OnboardingStatus,
	OnboardOrganisationRequest,
	OnboardOrganisationResponse,
	OnboardToProduct,
	OpenedAccountFields,
	Operation,
	OperationSummary,
	OperationType,
	OpsPlatformUserInfo,
	Organisation,
	OrganisationFees,
	OrganisationFeesV2,
	OrganisationOnboardingEntry,
	OrganisationOnboardingsResponse,
	OrganisationView,
	OverwriteOnboarding,
	page_size,
	PageMetadata,
	Party,
	passport,
	Passport,
	PatchAccountRequest,
	PatchFeeRequest,
	payload,
	Payment,
	PaymentDetails,
	PaymentFailure,
	PaymentScheme,
	PendingAccount,
	PendingAccountFields,
	PendingReason,
	Permission,
	PhotoCheck,
	PortalBatch,
	PortalFeeDebit,
	PortalFeeSourceRef,
	PortalFxTransfer,
	PortalInternalTransfer,
	PortalLocalPayment,
	PortalLocalReturnPayment,
	PortalRemitterDetails,
	PortalTransaction,
	PortalUserInfo,
	Product,
	ProductOnboarding,
	ProvisionClientMoney,
	ProvisionClientMoneyResponse,
	ProvisionSubAccount,
	ProvisionSubAccountResponse,
	PublicKeyResponse,
	PurposeCode,
	QuestionnaireResponse,
	QuestionResponse,
	Rate,
	ReadFeeSpecCondition,
	ReadFeeSpecV3,
	ReadFeeSpecV4,
	ReadOrgFeeResponse,
	ReadOrgFeeResponseV2,
	ReadOrgFeeResponseV3,
	ReadOrgFeeResponseV4,
	ReadOverrideFeeType,
	RecommendationResponse,
	ReflectExecutedRequest,
	ReflectFailedRequest,
	ReflectOnboarding,
	ReflectReturnedPaymentRequest,
	ReflectReturnedPaymentResponse,
	ReflectSubmittedRequest,
	ReflectSystemAccountAsCashManagementRequest,
	ReflectSystemAccountAsCashManagementResponse,
	RegisterInvitedUserRequest,
	RegisterPublicKeyRequest,
	RegisterUserRequest,
	RegisterUserResponse,
	RegulatoryLicenseType,
	RejectApprovalRequestRequest,
	RejectionReason,
	RemitterDetails,
	ReportId,
	ReportRequest,
	ReportResponse,
	ReportStatus,
	RequestAdvisoryRequest,
	RequestCashInterestChangeRequest,
	RequestCashInterestChangeResponse,
	RequestedMonth,
	RequestMonthlyStatementsRequest,
	RequestMonthlyStatementsResponse,
	ResourceAction,
	ResourcePayload,
	ResourcePayloadRequest,
	ResourcePayloadResponse,
	ResourceType,
	RetryCashManagementTransferResponse,
	RetryDlqMessageOutcome,
	RetryDlqMessagesRequest,
	RetryDlqMessagesResponse,
	RetryOnboarding,
	RetryPendingSubAccountsRequest,
	RetryPendingSubAccountsResponse,
	RetrySubAccountOutcome,
	RetryTransferResponse,
	Returned,
	RiskLevel,
	Role,
	RungResponse,
	SafeguardedForex,
	SafeguardedPayment,
	SafeguardedPaymentState,
	SaveBeneficiaryRequest,
	ScanDetails,
	SearchAccountsPagedResponse,
	SearchCustomersPagedResponse,
	SearchPortalTransactionsPagedResponse,
	SelectedOptionRequest,
	SharedPendingReason,
	SleeveResponse,
	SleeveTargetResponse,
	SortOrder,
	state,
	Status,
	status,
	StoreFeeRequest,
	SubAccountType,
	SubmittedAnswerRequest,
	TaxId,
	TaxIdType,
	TierBasis,
	Transaction,
	transaction_Batch,
	transaction_type,
	TransactionsStatementPayload,
	TransactionState,
	Transfer,
	TransferAccountType,
	uae_id,
	UnonboardOrganisationRequest,
	update_OperationType,
	UpdateCustomerApprovalStatusRequest,
	UpdateFeatureFlagRequest,
	UpdateFeeSpec,
	UpdateOrganisationRequest,
	UpdateShareholderRequest,
	UpdateWebhooksRequest,
	UploadBatchFileRequest,
	UploadDocumentRequest,
	UploadDocumentResponse,
	User,
	UserRolesRequest,
	VariableFee,
	Vat,
	VerificationResult,
	WatchlistCheck,
	WatchlistRecord,
	WebhookCategory,
	WebhookConfigRequest,
	WebhookEntry,
	WebhookGroup,
	WebhookType,
	WireBankDetails,
	WriteFeeSpec,
	WriteFeeSpecCondition,
	WriteOverrideFeeType,
	zand_CreatePrimaryAccountResponse,
	ZenusPendingReason,
};

const endpoints = makeApi([
	{
		alias: "authLogin",
		errors: [
			{
				description: `Redirected to auth0`,
				schema: z.void(),
				status: 302,
			},
		],
		method: "get",
		path: "/auth/login",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "authLogout",
		errors: [
			{
				description: `Redirected to logout page`,
				schema: z.void(),
				status: 302,
			},
		],
		method: "get",
		path: "/auth/logout",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "authUserInfo",
		method: "get",
		path: "/auth/user-info",
		requestFormat: "json",
		response: PortalUserInfo,
	},
	{
		alias: "authorizeCustomerPayment",
		errors: [
			{
				description: `Unable to complete request with data provided`,
				schema: z.void(),
				status: 400,
			},
			{
				description: `Account not found`,
				schema: z.void(),
				status: 404,
			},
			{
				description: `Idempotency-Key concurrency conflict`,
				schema: z.void(),
				status: 409,
			},
			{
				description: `Idempotency-Key reuse for a different request`,
				schema: z.void(),
				status: 422,
			},
		],
		method: "post",
		parameters: [
			{
				name: "body",
				schema: CreateCustomerPaymentRequest,
				type: "Body",
			},
			{
				name: "Idempotency-Key",
				schema: z.string().optional(), // Overridden as optional. Added via a plugin()
				type: "Header",
			},
			{
				name: "account_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/accounts/:account_id/payments",
		requestFormat: "json",
		response: z.object({ redirect_url: z.string() }),
	},
	{
		alias: "getAccountStatementLetter",
		errors: [
			{
				description: `Account not found`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "get",
		parameters: [
			{
				name: "year",
				schema: z.number().int(),
				type: "Query",
			},
			{
				name: "month",
				schema: z.number().int().gte(0),
				type: "Query",
			},
			{
				name: "account_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/accounts/:account_id/statement-letter",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "listApprovalRequests",
		errors: [
			{
				description: `Forbidden`,
				schema: z.void(),
				status: 403,
			},
		],
		method: "get",
		parameters: [
			{
				name: "resource_type",
				schema: z.enum(["payment", "batch_payment"]).optional(),
				type: "Query",
			},
			{
				name: "state",
				schema: z.enum(["pending", "approved", "rejected"]).optional(),
				type: "Query",
			},
			{
				name: "cursor",
				schema: cursor,
				type: "Query",
			},
			{
				name: "page_size",
				schema: z.number().int().gte(0).optional(),
				type: "Query",
			},
			{
				name: "maker_filter",
				schema: z.enum(["all", "own", "others"]).optional(),
				type: "Query",
			},
		],
		path: "/internal/v1/approval-requests",
		requestFormat: "json",
		response: ListApprovalRequestsResponse,
	},
	{
		alias: "initiateApprovalRequest",
		errors: [
			{
				description: `Unable to complete request with data provided`,
				schema: z.void(),
				status: 400,
			},
		],
		method: "post",
		parameters: [
			{
				name: "body",
				schema: InitiateApprovalRequestRequest,
				type: "Body",
			},
		],
		path: "/internal/v1/approval-requests",
		requestFormat: "json",
		response: z.object({ approval_request_id: z.string() }),
	},
	{
		alias: "getApprovalRequest",
		description: `- Users with &#x60;read:approval_requests&#x60; scope can view any request in the organisation
- Users with only &#x60;create:approval_requests&#x60; scope can only view their own requests
- Users without either scope receive a 403 Forbidden response`,
		errors: [
			{
				description: `Forbidden`,
				schema: z.void(),
				status: 403,
			},
			{
				description: `Approval request not found`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "get",
		parameters: [
			{
				name: "approval_request_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/approval-requests/:approval_request_id",
		requestFormat: "json",
		response: ApprovalRequestResponse,
	},
	{
		alias: "approveApprovalRequest",
		description: `Initiates an Auth0 PAR (Pushed Authorization Request) SCA flow. The response
contains a redirect URL to Auth0&#x27;s consent screen. After the checker consents,
Auth0 redirects to the approval callback endpoint which finalizes the approval.
SCA is only required for CreatePayment requests.
Other resource types (e.g. future UpdateAccount) are approved directly.`,
		errors: [
			{
				description: `Approval request not found`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "patch",
		parameters: [
			{
				name: "approval_request_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/approval-requests/:approval_request_id/approve",
		requestFormat: "json",
		response: ApproveApprovalRequestResponse,
	},
	{
		alias: "rejectApprovalRequest",
		errors: [
			{
				description: `Unable to complete request with data provided`,
				schema: z.void(),
				status: 400,
			},
			{
				description: `Approval request not found`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "patch",
		parameters: [
			{
				name: "body",
				schema: z.object({ comment: z.string() }),
				type: "Body",
			},
			{
				name: "approval_request_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/approval-requests/:approval_request_id/reject",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "internalEnableBankIntegration",
		method: "post",
		parameters: [
			{
				name: "body",
				schema: z.object({ bank: z.string() }),
				type: "Body",
			},
		],
		path: "/internal/v1/bank-integrations/enable",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "listBeneficiaries",
		method: "get",
		parameters: [
			{
				name: "cursor",
				schema: cursor,
				type: "Query",
			},
			{
				name: "page_size",
				schema: z.number().int().gte(0).optional(),
				type: "Query",
			},
			{
				name: "display_name_search",
				schema: z.string().optional(),
				type: "Query",
			},
			{
				name: "currency",
				schema: z
					.enum(["AED", "USD", "JOD", "EGP", "SAR", "GBP", "EUR", "HKD", "CNH", "SGD"])
					.optional(),
				type: "Query",
			},
			{
				name: "sort_by",
				schema: z
					.enum([
						"display_name",
						"account_type",
						"account_number",
						"currency",
						"created_timestamp",
					])
					.optional(),
				type: "Query",
			},
			{
				name: "sort_order",
				schema: z.enum(["ascending", "descending"]).optional(),
				type: "Query",
			},
		],
		path: "/internal/v1/beneficiaries",
		requestFormat: "json",
		response: ListBeneficiariesPagedResponse,
	},
	{
		alias: "createBeneficiary",
		errors: [
			{
				description: `Invalid beneficiary data`,
				schema: z.void(),
				status: 400,
			},
		],
		method: "post",
		parameters: [
			{
				name: "body",
				schema: SaveBeneficiaryRequest,
				type: "Body",
			},
		],
		path: "/internal/v1/beneficiaries",
		requestFormat: "json",
		response: BeneficiaryResponse,
	},
	{
		alias: "getBeneficiary",
		errors: [
			{
				description: `Beneficiary not found`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "get",
		parameters: [
			{
				name: "id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/beneficiaries/:id",
		requestFormat: "json",
		response: BeneficiaryResponse,
	},
	{
		alias: "updateBeneficiary",
		errors: [
			{
				description: `Invalid update data`,
				schema: z.void(),
				status: 400,
			},
			{
				description: `Beneficiary not found`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "put",
		parameters: [
			{
				name: "body",
				schema: SaveBeneficiaryRequest,
				type: "Body",
			},
			{
				name: "id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/beneficiaries/:id",
		requestFormat: "json",
		response: BeneficiaryResponse,
	},
	{
		alias: "deleteBeneficiary",
		errors: [
			{
				description: `Beneficiary not found`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "delete",
		parameters: [
			{
				name: "id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/beneficiaries/:id",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "listCashManagementAdminTransactions",
		method: "get",
		parameters: [
			{
				name: "cursor",
				schema: cursor,
				type: "Query",
			},
			{
				name: "page_size",
				schema: page_size,
				type: "Query",
			},
			{
				name: "organisation_id",
				schema: cursor,
				type: "Query",
			},
			{
				name: "cash_management_account_id",
				schema: cursor,
				type: "Query",
			},
			{
				name: "transaction_type",
				schema: transaction_type,
				type: "Query",
			},
			{
				name: "state",
				schema: state,
				type: "Query",
			},
			{
				name: "from_timestamp",
				schema: cursor,
				type: "Query",
			},
			{
				name: "to_timestamp",
				schema: cursor,
				type: "Query",
			},
		],
		path: "/internal/v1/cash-management-admin/transactions",
		requestFormat: "json",
		response: ListCashManagementTransactionsResponse,
	},
	{
		alias: "retryCashManagementTransferNeedingFunds",
		description: `On a clearing -&gt; cash-management transfer, when the cash-management transfer settlement pool
account has not enough funds to cover the transfer, the pool -&gt; cash-management-account leg is
rejected into the recoverable &#x60;needs_funds&#x60; state instead of failing outright.

This endpoint re-attempts that leg: it re-issues the fund reservation on the pool, after which
the transfer resumes the normal settlement flow.
If the pool is still lacking funds, the transfer stays in &#x60;needs_funds&#x60; and the retry can be repeated.`,
		errors: [
			{
				description: `Outbound transfer not found`,
				schema: z.void(),
				status: 404,
			},
			{
				description: `Outbound transfer is not awaiting funds, or Idempotency-Key concurrency conflict`,
				schema: z.void(),
				status: 409,
			},
			{
				description: `Idempotency-Key reuse for a different request`,
				schema: z.void(),
				status: 422,
			},
		],
		method: "post",
		parameters: [
			{
				name: "transfer_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "Idempotency-Key",
				schema: z.string().optional(), // Overridden as optional. Added via a plugin()
				type: "Header",
			},
		],
		path: "/internal/v1/cash-management-admin/transfers/:transfer_id/retry",
		requestFormat: "json",
		response: z.object({ transfer_id: z.string() }),
	},
	{
		alias: "internalCreateClient",
		errors: [
			{
				description: `Unable to complete request with data provided`,
				schema: z.void(),
				status: 400,
			},
			{
				description: `Organisation not found`,
				schema: z.void(),
				status: 404,
			},
			{
				description: `Idempotency-Key concurrency conflict`,
				schema: z.void(),
				status: 409,
			},
			{
				description: `Idempotency-Key reuse for a different request`,
				schema: z.void(),
				status: 422,
			},
		],
		method: "post",
		parameters: [
			{
				name: "Idempotency-Key",
				schema: z.string().optional(), // Overridden as optional. Added via a plugin()
				type: "Header",
			},
		],
		path: "/internal/v1/clients",
		requestFormat: "json",
		response: CreateClientResponse,
	},
	{
		alias: "internalUpdateClient",
		errors: [
			{
				description: `Organisation not found`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "patch",
		path: "/internal/v1/clients",
		requestFormat: "json",
		response: CreateClientResponse,
	},
	{
		alias: "getCustomerAedOnboarding",
		method: "get",
		parameters: [
			{
				name: "customer_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/customers/:customer_id/onboarding/aed",
		requestFormat: "json",
		response: AedOnboarding,
	},
	{
		alias: "getDeadLetterQueues",
		errors: [
			{
				description: `Unable to complete request with data provided`,
				schema: z.void(),
				status: 400,
			},
		],
		method: "get",
		path: "/internal/v1/dead-letter-queues",
		requestFormat: "json",
		response: GetDlqsResponse,
	},
	{
		alias: "dropDeadLetterQueueMessages",
		errors: [
			{
				description: `Unable to complete request with data provided`,
				schema: z.void(),
				status: 400,
			},
		],
		method: "get",
		parameters: [
			{
				name: "body",
				schema: DropDlqMessagesRequest,
				type: "Body",
			},
			{
				name: "queue_name",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/dead-letter-queues/:queue_name/drop",
		requestFormat: "json",
		response: DropDlqMessagesResponse,
	},
	{
		alias: "getDeadLetterQueueMessages",
		description: `## Caveats

- The maximum number of returned message is capped at 100.
- Due to the guarantees of SQS, it is possible this endpoint will return _less_ messages
  than what are actually available in the queue.`,
		errors: [
			{
				description: `Unable to complete request with data provided`,
				schema: z.void(),
				status: 400,
			},
		],
		method: "get",
		parameters: [
			{
				name: "queue_name",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/dead-letter-queues/:queue_name/messages",
		requestFormat: "json",
		response: GetDlqMessagesResponse,
	},
	{
		alias: "retryDeadLetterQueueMessages",
		description: `## Caveats

- Due to the guarantees of SQS, it is possible this endpoint will return a &#x60;not_found&#x60;
  error for some messages even though they actually exist in the DLQ.`,
		errors: [
			{
				description: `Unable to complete request with data provided`,
				schema: z.void(),
				status: 400,
			},
		],
		method: "get",
		parameters: [
			{
				name: "body",
				schema: RetryDlqMessagesRequest,
				type: "Body",
			},
			{
				name: "queue_name",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/dead-letter-queues/:queue_name/retry",
		requestFormat: "json",
		response: RetryDlqMessagesResponse,
	},
	{
		alias: "internalListFeatureFlags",
		method: "get",
		path: "/internal/v1/feature-flags",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "internalUpdateFeatureFlag",
		method: "patch",
		parameters: [
			{
				name: "body",
				schema: UpdateFeatureFlagRequest,
				type: "Body",
			},
		],
		path: "/internal/v1/feature-flags",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "getNexusVolumePortal",
		description: `A subscriber gets the volume its fee waiver applied to; anyone else gets the volume that would
have qualified, which is what an upsell is measured against, so which one comes back follows
the organisation&#x27;s own subscription.`,
		method: "get",
		path: "/internal/v1/nexus/volume",
		requestFormat: "json",
		response: NexusVolumeResponse,
	},
	{
		alias: "getOperations",
		method: "get",
		parameters: [
			{
				name: "operation_type",
				schema: z
					.enum(["Created", "Submitted", "Executed", "Failed", "Settled"])
					.optional(),
				type: "Query",
			},
			{
				name: "cursor",
				schema: cursor,
				type: "Query",
			},
			{
				name: "page_size",
				schema: z.number().int().gte(0).optional(),
				type: "Query",
			},
		],
		path: "/internal/v1/operations",
		requestFormat: "json",
		response: z.array(OperationSummary),
	},
	{
		alias: "getOperation",
		method: "get",
		parameters: [
			{
				name: "operation_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/operations/:operation_id",
		requestFormat: "json",
		response: Operation,
	},
	{
		alias: "executeOperation",
		method: "patch",
		parameters: [
			{
				name: "body",
				schema: update_OperationType,
				type: "Body",
			},
			{
				name: "operation_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/operations/:operation_id/execute",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "internalGetOrganisationMetadata",
		method: "get",
		path: "/internal/v1/organisations",
		requestFormat: "json",
		response: OrganisationView,
	},
	{
		alias: "internalCreateProductionOrganisation",
		method: "post",
		parameters: [
			{
				name: "body",
				schema: CreateOrganisationRequest,
				type: "Body",
			},
		],
		path: "/internal/v1/organisations",
		requestFormat: "json",
		response: z.object({ org_id: z.string() }),
	},
	{
		alias: "internalUpdateOrganisationMetadata",
		method: "patch",
		parameters: [
			{
				name: "body",
				schema: UpdateOrganisationRequest,
				type: "Body",
			},
		],
		path: "/internal/v1/organisations",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "retoolGetOrgMetadata",
		method: "get",
		parameters: [
			{
				name: "org_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:org_id",
		requestFormat: "json",
		response: OrganisationView,
	},
	{
		alias: "internalListAllCustomerAccountsForOrganisation",
		method: "get",
		parameters: [
			{
				name: "customer_id",
				schema: z.string().optional(),
				type: "Query",
			},
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/accounts",
		requestFormat: "json",
		response: z.array(Account),
	},
	{
		alias: "internalGetCustomerAccount",
		method: "get",
		parameters: [
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "account_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/accounts/:account_id",
		requestFormat: "json",
		response: Account,
	},
	{
		alias: "getOrganisationAccountStatementLetter",
		errors: [
			{
				description: `Account not found`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "get",
		parameters: [
			{
				name: "year",
				schema: z.number().int(),
				type: "Query",
			},
			{
				name: "month",
				schema: z.number().int().gte(0),
				type: "Query",
			},
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "account_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/accounts/:account_id/statement-letter",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "retoolArchiveOrganisation",
		description: `Archiving is reversible and destroys no data: accounts, customers and ledger
history are all retained.`,
		errors: [
			{
				description: `Organisation not found`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "patch",
		parameters: [
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/archive",
		requestFormat: "json",
		response: z.object({ archived_at: z.string().datetime() }),
	},
	{
		alias: "createCashManagementAccount",
		errors: [
			{
				description: `Unable to complete request with data provided`,
				schema: z.void(),
				status: 400,
			},
			{
				description: `Customer not found`,
				schema: z.void(),
				status: 404,
			},
			{
				description: `Idempotency-Key concurrency conflict`,
				schema: z.void(),
				status: 409,
			},
			{
				description: `Idempotency-Key reuse for a different request`,
				schema: z.void(),
				status: 422,
			},
		],
		method: "post",
		parameters: [
			{
				name: "body",
				schema: CreateCashManagementAccountRequest,
				type: "Body",
			},
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "Idempotency-Key",
				schema: z.string().optional(), // Overridden as optional. Added via a plugin()
				type: "Header",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/cash-management/accounts",
		requestFormat: "json",
		response: z.object({ account_id: z.string() }),
	},
	{
		alias: "listCashInterestAprTiers",
		errors: [
			{
				description: `The organisation&#x27;s bank does not offer cash interest`,
				schema: z.void(),
				status: 400,
			},
			{
				description: `The organisation has no provisioned cash management account, or holds them at more than one bank`,
				schema: z.void(),
				status: 422,
			},
		],
		method: "get",
		parameters: [
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/cash-management/apr-tiers",
		requestFormat: "json",
		response: z.array(AprTierResponse),
	},
	{
		alias: "internalRetoolGetOrgConfig",
		errors: [
			{
				description: `Organisation not found`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "get",
		parameters: [
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/config",
		requestFormat: "json",
		response: GetOrganisationConfigResponse,
	},
	{
		alias: "listCustomers",
		method: "get",
		parameters: [
			{
				name: "reference",
				schema: z.string().optional(),
				type: "Query",
			},
			{
				name: "customer_type",
				schema: z.enum(["individual", "business"]).optional(),
				type: "Query",
			},
			{
				name: "cursor",
				schema: cursor,
				type: "Query",
			},
			{
				name: "page_size",
				schema: z.number().int().gte(0).optional(),
				type: "Query",
			},
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/customers",
		requestFormat: "json",
		response: ListCustomersResponse,
	},
	{
		alias: "getCustomerInternal",
		method: "get",
		parameters: [
			{
				name: "customer_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/customers/:customer_id",
		requestFormat: "json",
		response: GetCustomerInternalResponse,
	},
	{
		alias: "updateCustomerApprovalStatus",
		method: "patch",
		parameters: [
			{
				name: "body",
				schema: UpdateCustomerApprovalStatusRequest,
				type: "Body",
			},
			{
				name: "customer_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/customers/:customer_id/approval-status",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "manuallyCompleteOnboarding",
		errors: [
			{
				description: `Invalid request`,
				schema: ErrorResponse,
				status: 400,
			},
			{
				description: `Authentication error`,
				schema: ErrorResponse,
				status: 401,
			},
			{
				description: `Internal server error`,
				schema: ErrorResponse,
				status: 500,
			},
		],
		method: "post",
		parameters: [
			{
				name: "body",
				schema: ManuallyCompleteOnboarding,
				type: "Body",
			},
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "customer_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/customers/:customer_id/complete-onboarding",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "internalSubmitDocument",
		errors: [
			{
				description: `Invalid request`,
				schema: ErrorResponse,
				status: 400,
			},
			{
				description: `Authentication error`,
				schema: ErrorResponse,
				status: 401,
			},
			{
				description: `Internal server error`,
				schema: ErrorResponse,
				status: 500,
			},
		],
		method: "post",
		parameters: [
			{
				name: "body",
				schema: z.object({ document_id: z.string() }),
				type: "Body",
			},
			{
				name: "customer_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/customers/:customer_id/documents",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "internalUnlinkDocument",
		errors: [
			{
				description: `Invalid request`,
				schema: ErrorResponse,
				status: 400,
			},
			{
				description: `Authentication error`,
				schema: ErrorResponse,
				status: 401,
			},
			{
				description: `Internal server error`,
				schema: ErrorResponse,
				status: 500,
			},
		],
		method: "delete",
		parameters: [
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "customer_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "document_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/customers/:customer_id/documents/:document_id",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "getProductOnboarding",
		errors: [
			{
				description: `Invalid request`,
				schema: ErrorResponse,
				status: 400,
			},
			{
				description: `Authentication error`,
				schema: ErrorResponse,
				status: 401,
			},
			{
				description: `Internal server error`,
				schema: ErrorResponse,
				status: 500,
			},
		],
		method: "get",
		parameters: [
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "customer_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "product",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/customers/:customer_id/onboarding/products/:product",
		requestFormat: "json",
		response: ProductOnboarding,
	},
	{
		alias: "onboardCustomerToProduct",
		errors: [
			{
				description: `Invalid request`,
				schema: ErrorResponse,
				status: 400,
			},
			{
				description: `Authentication error`,
				schema: ErrorResponse,
				status: 401,
			},
			{
				description: `Internal server error`,
				schema: ErrorResponse,
				status: 500,
			},
		],
		method: "post",
		parameters: [
			{
				name: "body",
				schema: z.object({ command_id: z.string() }),
				type: "Body",
			},
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "customer_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "product",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/customers/:customer_id/onboarding/products/:product",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "overwriteOnboarding",
		errors: [
			{
				description: `Invalid request`,
				schema: ErrorResponse,
				status: 400,
			},
			{
				description: `Authentication error`,
				schema: ErrorResponse,
				status: 401,
			},
			{
				description: `Internal server error`,
				schema: ErrorResponse,
				status: 500,
			},
		],
		method: "post",
		parameters: [
			{
				name: "body",
				schema: OverwriteOnboarding,
				type: "Body",
			},
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "customer_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/customers/:customer_id/overwrite-onboarding",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "reflectOnboarding",
		errors: [
			{
				description: `Invalid request`,
				schema: ErrorResponse,
				status: 400,
			},
			{
				description: `Authentication error`,
				schema: ErrorResponse,
				status: 401,
			},
			{
				description: `Internal server error`,
				schema: ErrorResponse,
				status: 500,
			},
		],
		method: "post",
		parameters: [
			{
				name: "body",
				schema: ReflectOnboarding,
				type: "Body",
			},
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "customer_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/customers/:customer_id/reflect-onboarding",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "retryOnboarding",
		errors: [
			{
				description: `Invalid request`,
				schema: ErrorResponse,
				status: 400,
			},
			{
				description: `Authentication error`,
				schema: ErrorResponse,
				status: 401,
			},
			{
				description: `Internal server error`,
				schema: ErrorResponse,
				status: 500,
			},
		],
		method: "post",
		parameters: [
			{
				name: "body",
				schema: RetryOnboarding,
				type: "Body",
			},
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "customer_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/customers/:customer_id/retry",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "updateShareholderHandler",
		errors: [
			{
				description: `Invalid request`,
				schema: ErrorResponse,
				status: 400,
			},
			{
				description: `Customer or shareholder not found`,
				schema: ErrorResponse,
				status: 404,
			},
			{
				description: `Internal server error`,
				schema: ErrorResponse,
				status: 500,
			},
		],
		method: "patch",
		parameters: [
			{
				name: "body",
				schema: UpdateShareholderRequest,
				type: "Body",
			},
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "customer_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "shareholder_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/customers/:customer_id/shareholders/:shareholder_id",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "unlinkShareholderHandler",
		errors: [
			{
				description: `Invalid request`,
				schema: ErrorResponse,
				status: 400,
			},
			{
				description: `Customer or shareholder not found`,
				schema: ErrorResponse,
				status: 404,
			},
			{
				description: `Internal server error`,
				schema: ErrorResponse,
				status: 500,
			},
		],
		method: "delete",
		parameters: [
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "customer_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "shareholder_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/customers/:customer_id/shareholders/:shareholder_id/unlink",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "internalUploadDocument",
		method: "post",
		parameters: [
			{
				description: `Document`,
				name: "body",
				schema: UploadDocumentRequest,
				type: "Body",
			},
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/documents",
		requestFormat: "form-data",
		response: z.object({ document_id: z.string() }),
	},
	{
		alias: "getDocument",
		method: "get",
		parameters: [
			{
				name: "document_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/documents/:document_id",
		requestFormat: "json",
		response: Document,
	},
	{
		alias: "patchOrganisationFee",
		method: "patch",
		parameters: [
			{
				name: "body",
				schema: PatchFeeRequest,
				type: "Body",
			},
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/fee",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "getOrganisationFees",
		errors: [
			{
				description: `There is no fee override for the organisation`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "get",
		parameters: [
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/fees",
		requestFormat: "json",
		response: ReadOrgFeeResponse,
	},
	{
		alias: "storeOrganisationFees",
		method: "post",
		parameters: [
			{
				name: "body",
				schema: StoreFeeRequest,
				type: "Body",
			},
			{
				name: "force",
				schema: z.boolean().optional(),
				type: "Query",
			},
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/fees",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "createInternalTransferToPrimaryAccount",
		errors: [
			{
				description: `Unable to complete request with data provided`,
				schema: z.void(),
				status: 400,
			},
			{
				description: `Idempotency-Key concurrency conflict`,
				schema: z.void(),
				status: 409,
			},
			{
				description: `Idempotency-Key reuse for a different request`,
				schema: z.void(),
				status: 422,
			},
		],
		method: "post",
		parameters: [
			{
				name: "body",
				schema: CreateInternalTransferToPrimaryAccountRequest,
				type: "Body",
			},
			{
				name: "Idempotency-Key",
				schema: z.string().optional(), // Overridden as optional. Added via a plugin()
				type: "Header",
			},
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/internal-transfers-to-primary-account",
		requestFormat: "json",
		response: z.object({ transaction_id: z.string() }),
	},
	{
		alias: "retoolInviteOrganisationUser",
		method: "post",
		parameters: [
			{
				name: "body",
				schema: InviteUserRequest,
				type: "Body",
			},
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/invite",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "onboardOrganisation",
		description: `Enables the currency for the organisation without requiring a primary
account: the organisation can then create virtual accounts and onboard
customers for that currency. Stored as a new version row in
&#x60;organisation_onboardings&#x60;; already-onboarded accounts are a no-op.`,
		errors: [
			{
				description: `Account is not a pooled internal account, currency mismatch, or the organisation takes fees from the primary account (pass &#x60;force&#x60; to override)`,
				schema: z.void(),
				status: 400,
			},
			{
				description: `Organisation not found`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "post",
		parameters: [
			{
				name: "body",
				schema: OnboardOrganisationRequest,
				type: "Body",
			},
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/onboard",
		requestFormat: "json",
		response: OnboardOrganisationResponse,
	},
	{
		alias: "getOrganisationOnboardings",
		method: "get",
		parameters: [
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/onboardings",
		requestFormat: "json",
		response: OrganisationOnboardingsResponse,
	},
	{
		alias: "createOrganisationPrimaryAccount",
		errors: [
			{
				description: `Unable to complete request with data provided`,
				schema: z.void(),
				status: 400,
			},
			{
				description: `Customer not found`,
				schema: z.void(),
				status: 404,
			},
			{
				description: `Idempotency-Key concurrency conflict`,
				schema: z.void(),
				status: 409,
			},
			{
				description: `Idempotency-Key reuse for a different request`,
				schema: z.void(),
				status: 422,
			},
		],
		method: "post",
		parameters: [
			{
				name: "body",
				schema: CreateAccountRequest,
				type: "Body",
			},
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/primary-accounts",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "retoolGetMonthlyStatements",
		errors: [
			{
				description: `Invalid month range`,
				schema: z.void(),
				status: 400,
			},
		],
		method: "get",
		parameters: [
			{
				name: "start_year",
				schema: z.number().int(),
				type: "Query",
			},
			{
				name: "start_month",
				schema: z.number().int().gte(0),
				type: "Query",
			},
			{
				name: "end_year",
				schema: z.number().int(),
				type: "Query",
			},
			{
				name: "end_month",
				schema: z.number().int().gte(0),
				type: "Query",
			},
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/statements/monthly",
		requestFormat: "json",
		response: GetMonthlyStatementsResponse,
	},
	{
		alias: "retoolRequestMonthlyStatements",
		description: `Idempotent per organisation and month: a statement already rendered is not rendered again
unless &#x60;force&#x60; is set. The range may not reach past the last completed month.`,
		errors: [
			{
				description: `Invalid month range`,
				schema: z.void(),
				status: 400,
			},
			{
				description: `No such organisation`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "post",
		parameters: [
			{
				name: "body",
				schema: RequestMonthlyStatementsRequest,
				type: "Body",
			},
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/statements/monthly",
		requestFormat: "json",
		response: RequestMonthlyStatementsResponse,
	},
	{
		alias: "reflectSystemAccountAsCashManagement",
		description: `Unlike account creation, this does not ask the bank to open anything — it mirrors an account
that already exists at the bank (identified by its existing &#x60;account_identifier&#x60;) into the
org&#x27;s pooled physical account for the currency, driving the normal
&#x60;OpenAccount -&gt; AccountOpenedV2&#x60; ledger flow. The org must already have a pooled internal
account for the given currency.`,
		errors: [
			{
				description: `Unable to complete request with data provided`,
				schema: z.void(),
				status: 400,
			},
		],
		method: "post",
		parameters: [
			{
				name: "body",
				schema: ReflectSystemAccountAsCashManagementRequest,
				type: "Body",
			},
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/system-accounts/reflect-as-cash-management",
		requestFormat: "json",
		response: z.object({ account_id: z.string() }),
	},
	{
		alias: "reflectReturnedPayment",
		errors: [
			{
				description: `Unable to complete request with data provided`,
				schema: z.void(),
				status: 400,
			},
			{
				description: `Transaction not found`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "patch",
		parameters: [
			{
				name: "body",
				schema: ReflectReturnedPaymentRequest,
				type: "Body",
			},
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "original_transaction_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/transactions/:original_transaction_id/reflect-returned",
		requestFormat: "json",
		response: z.object({ transaction_id: z.string() }),
	},
	{
		alias: "reflectExecutedTransaction",
		errors: [
			{
				description: `Unable to complete request with data provided`,
				schema: z.void(),
				status: 400,
			},
			{
				description: `Transaction not found`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "patch",
		parameters: [
			{
				name: "body",
				schema: z.object({ command_id: z.string() }),
				type: "Body",
			},
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "transaction_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/transactions/:transaction_id/reflect-executed",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "reflectFailedTransaction",
		errors: [
			{
				description: `Unable to complete request with data provided`,
				schema: z.void(),
				status: 400,
			},
			{
				description: `Transaction not found`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "patch",
		parameters: [
			{
				name: "body",
				schema: ReflectFailedRequest,
				type: "Body",
			},
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "transaction_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/transactions/:transaction_id/reflect-failed",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "reflectSubmittedTransaction",
		errors: [
			{
				description: `Unable to complete request with data provided`,
				schema: z.void(),
				status: 400,
			},
			{
				description: `Transaction not found`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "patch",
		parameters: [
			{
				name: "body",
				schema: ReflectSubmittedRequest,
				type: "Body",
			},
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "transaction_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/transactions/:transaction_id/reflect-submitted",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "retoolUnarchiveOrganisation",
		errors: [
			{
				description: `Organisation not found`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "patch",
		parameters: [
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/unarchive",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "unonboardOrganisation",
		errors: [
			{
				description: `Account is not a pooled internal account or currency mismatch`,
				schema: z.void(),
				status: 400,
			},
		],
		method: "post",
		parameters: [
			{
				name: "body",
				schema: UnonboardOrganisationRequest,
				type: "Body",
			},
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/unonboard",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "retoolListUsersForOrganisation",
		method: "get",
		parameters: [
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/users",
		requestFormat: "json",
		response: ListUsersForOrganisationResponse,
	},
	{
		alias: "internalRetoolListRolesForUser",
		method: "get",
		parameters: [
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "user_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/:organisation_id/users/:user_id/roles",
		requestFormat: "json",
		response: z.array(Role),
	},
	{
		alias: "internalRetoolListOrganisations",
		method: "get",
		path: "/internal/v1/organisations/all",
		requestFormat: "json",
		response: z.array(OrganisationView),
	},
	{
		alias: "getAllOrganisationsFees",
		errors: [
			{
				description: `Internal server error`,
				schema: z.void(),
				status: 500,
			},
		],
		method: "get",
		path: "/internal/v1/organisations/all/fees",
		requestFormat: "json",
		response: AllOrganisationsFeesResponse,
	},
	{
		alias: "internalGetOrgConfig",
		method: "get",
		path: "/internal/v1/organisations/config",
		requestFormat: "json",
		response: GetOrganisationConfigResponse,
	},
	{
		alias: "internalPortalUpdateWebhookConfig",
		method: "patch",
		parameters: [
			{
				name: "body",
				schema: z.object({ webhook_url: z.string().url() }),
				type: "Body",
			},
		],
		path: "/internal/v1/organisations/config/webhook",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "getEnabledCurrencies",
		method: "get",
		path: "/internal/v1/organisations/currencies",
		requestFormat: "json",
		response: z.record(EnabledForCurrency),
	},
	{
		alias: "getFees",
		errors: [
			{
				description: `There is no fee override for the organisation`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "get",
		path: "/internal/v1/organisations/fees",
		requestFormat: "json",
		response: LocalReadFee,
	},
	{
		alias: "internalListOrganisationPublicKeys",
		method: "get",
		path: "/internal/v1/organisations/public-keys",
		requestFormat: "json",
		response: z.array(PublicKeyResponse),
	},
	{
		alias: "internalRegisterOrganisationPublicKey",
		errors: [
			{
				description: `The public key is not a valid P-256 SPKI PEM`,
				schema: z.void(),
				status: 400,
			},
			{
				description: `A public key with this material already exists`,
				schema: z.void(),
				status: 409,
			},
		],
		method: "post",
		parameters: [
			{
				name: "body",
				schema: RegisterPublicKeyRequest,
				type: "Body",
			},
		],
		path: "/internal/v1/organisations/public-keys",
		requestFormat: "json",
		response: PublicKeyResponse,
	},
	{
		alias: "internalRevokeOrganisationPublicKey",
		errors: [
			{
				description: `No active public key with that id for the organisation`,
				schema: z.void(),
				status: 404,
			},
			{
				description: `The public key is already revoked`,
				schema: z.void(),
				status: 409,
			},
		],
		method: "delete",
		parameters: [
			{
				name: "keyid",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/public-keys/:keyid",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "internalListUsersForOrganisation",
		method: "get",
		path: "/internal/v1/organisations/users",
		requestFormat: "json",
		response: ListUsersForOrganisationResponse,
	},
	{
		alias: "internalInviteOrganisationUser",
		method: "post",
		parameters: [
			{
				name: "body",
				schema: InviteUserRequest,
				type: "Body",
			},
		],
		path: "/internal/v1/organisations/users",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "internalDeleteUserFromOrganisation",
		method: "delete",
		parameters: [
			{
				name: "user_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/users/:user_id",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "internalAssignRoleToUser",
		method: "patch",
		parameters: [
			{
				name: "body",
				schema: z.object({ role_name: z.string() }),
				type: "Body",
			},
			{
				name: "user_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/users/:user_id",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "internalListRolesForUser",
		method: "get",
		parameters: [
			{
				name: "user_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/users/:user_id/roles",
		requestFormat: "json",
		response: z.array(Role),
	},
	{
		alias: "internalAddRolesToUser",
		method: "post",
		parameters: [
			{
				name: "body",
				schema: UserRolesRequest,
				type: "Body",
			},
			{
				name: "user_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/users/:user_id/roles",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "internalRemoveRolesFromUser",
		method: "delete",
		parameters: [
			{
				name: "body",
				schema: UserRolesRequest,
				type: "Body",
			},
			{
				name: "user_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/organisations/users/:user_id/roles",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "registerInvitedUser",
		method: "patch",
		parameters: [
			{
				name: "body",
				schema: RegisterInvitedUserRequest,
				type: "Body",
			},
		],
		path: "/internal/v1/organisations/users/add",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "updateWebhooks",
		errors: [
			{
				description: `Unsupported webhook type`,
				schema: z.void(),
				status: 400,
			},
		],
		method: "put",
		parameters: [
			{
				name: "body",
				schema: UpdateWebhooksRequest,
				type: "Body",
			},
		],
		path: "/internal/v1/organisations/webhooks",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "listReports",
		method: "get",
		parameters: [
			{
				name: "payload",
				schema: payload,
				type: "Query",
			},
			{
				name: "status",
				schema: z.enum(["scheduled", "completed", "rejected"]).optional(),
				type: "Query",
			},
			{
				name: "cursor",
				schema: cursor,
				type: "Query",
			},
			{
				name: "page_size",
				schema: z.number().int().gte(0).optional(),
				type: "Query",
			},
		],
		path: "/internal/v1/reports",
		requestFormat: "json",
		response: ListReportsPagedResponse,
	},
	{
		alias: "getReport",
		errors: [
			{
				description: `No report found`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "get",
		parameters: [
			{
				name: "id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/reports/:id",
		requestFormat: "json",
		response: ReportResponse,
	},
	{
		alias: "retryPendingSubAccounts",
		method: "post",
		parameters: [
			{
				name: "body",
				schema: RetryPendingSubAccountsRequest,
				type: "Body",
			},
		],
		path: "/internal/v1/retry-pending-sub-accounts",
		requestFormat: "json",
		response: RetryPendingSubAccountsResponse,
	},
	{
		alias: "internalListRoles",
		method: "get",
		path: "/internal/v1/roles",
		requestFormat: "json",
		response: z.array(Role),
	},
	{
		alias: "internalListPermissionForRole",
		method: "get",
		parameters: [
			{
				name: "role_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/roles/:role_id",
		requestFormat: "json",
		response: z.array(Permission),
	},
	{
		alias: "requestAdvisory",
		errors: [
			{
				description: `The answers are invalid for the questionnaire`,
				schema: z.void(),
				status: 400,
			},
			{
				description: `No such customer for this organisation, or no product suits the submitted answers`,
				schema: z.void(),
				status: 404,
			},
			{
				description: `Idempotency-Key concurrency conflict, or the advisory was changed by a concurrent request`,
				schema: z.void(),
				status: 409,
			},
			{
				description: `Idempotency-Key reuse for a different request`,
				schema: z.void(),
				status: 422,
			},
		],
		method: "post",
		parameters: [
			{
				name: "body",
				schema: RequestAdvisoryRequest,
				type: "Body",
			},
			{
				name: "customer_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "Idempotency-Key",
				schema: z.string().optional(), // Overridden as optional. Added via a plugin()
				type: "Header",
			},
		],
		path: "/internal/v1/smart-treasury/customers/:customer_id/advisory",
		requestFormat: "json",
		response: AdvisoryResponse,
	},
	{
		alias: "decideAdvisory",
		errors: [
			{
				description: `No such customer or advisory for this organisation`,
				schema: z.void(),
				status: 404,
			},
			{
				description: `The advisory was changed by a concurrent request`,
				schema: z.void(),
				status: 409,
			},
		],
		method: "patch",
		parameters: [
			{
				name: "body",
				schema: AdvisoryDecisionRequest,
				type: "Body",
			},
			{
				name: "customer_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/smart-treasury/customers/:customer_id/advisory",
		requestFormat: "json",
		response: AdvisoryDecisionResponse,
	},
	{
		alias: "getQuestionnaire",
		method: "get",
		path: "/internal/v1/smart-treasury/questionnaire",
		requestFormat: "json",
		response: QuestionnaireResponse,
	},
	{
		alias: "listAllTransactions",
		method: "get",
		parameters: [
			{
				name: "batch_id",
				schema: z.string().optional(),
				type: "Query",
			},
			{
				name: "transaction_state",
				schema: z
					.enum(["Created", "Submitted", "Executed", "Failed", "Settled"])
					.optional(),
				type: "Query",
			},
			{
				name: "payment_type",
				schema: z
					.enum([
						"Local",
						"InternalTransfer",
						"FXTransfer",
						"LocalReturnPayment",
						"Fee",
						"Transfer",
					])
					.optional(),
				type: "Query",
			},
			{
				name: "customer_account_id",
				schema: z.string().optional(),
				type: "Query",
			},
			{
				name: "from",
				schema: z.string().datetime().optional(),
				type: "Query",
			},
			{
				name: "to",
				schema: z.string().datetime().optional(),
				type: "Query",
			},
			{
				name: "reference",
				schema: z.string().optional(),
				type: "Query",
			},
			{
				name: "cursor",
				schema: cursor,
				type: "Query",
			},
			{
				name: "page_size",
				schema: z.number().int().gte(0).optional(),
				type: "Query",
			},
		],
		path: "/internal/v1/transactions",
		requestFormat: "json",
		response: ListAllTransactionsPagedResponse,
	},
	{
		alias: "getInternalTransaction",
		errors: [
			{
				description: `Transaction not found`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "get",
		parameters: [
			{
				name: "transaction_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/transactions/:transaction_id",
		requestFormat: "json",
		response: Transaction,
	},
	{
		alias: "getTransactionFeeCalculation",
		errors: [
			{
				description: `There is no fee override for the transaction`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "get",
		parameters: [
			{
				name: "transaction_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/transactions/:transaction_id/fees",
		requestFormat: "json",
		response: FeeCalculation,
	},
	{
		alias: "authorizeBatchPayment",
		description: `Parses and validates the CSV, then immediately starts an Auth0 PAR flow.
The portal should redirect the user to the returned &#x60;redirect_url&#x60;. After the
user consents, Auth0 redirects to the callback endpoint which writes the batch
to the database and allows the cron job to process the payments.`,
		errors: [
			{
				description: `Unable to process batch file due to bad data`,
				schema: z.void(),
				status: 400,
			},
		],
		method: "post",
		parameters: [
			{
				name: "body",
				schema: UploadBatchFileRequest,
				type: "Body",
			},
		],
		path: "/internal/v1/transactions/batches/authorize",
		requestFormat: "json",
		response: AuthorizeBatchPaymentResponse,
	},
	{
		alias: "validateBatchPayment",
		errors: [
			{
				description: `Batch file is not valid`,
				schema: z.void(),
				status: 400,
			},
		],
		method: "post",
		parameters: [
			{
				name: "body",
				schema: UploadBatchFileRequest,
				type: "Body",
			},
		],
		path: "/internal/v1/transactions/batches/dry-run",
		requestFormat: "json",
		response: z.object({ number_of_payments: z.number().int().gte(0) }),
	},
	{
		alias: "retryTransferNeedingFunds",
		description: `On a cash-management -&gt; clearing transfer, when the clearing transfer settlement pool
account has not enough funds to cover the transfer, the pool -&gt; clearing-account leg is
rejected into the recoverable &#x60;needs_funds&#x60; state instead of failing outright.

This endpoint re-attempts that leg: it re-issues the fund reservation on the pool, after which
the transfer resumes the normal settlement flow.
If the pool is still lacking funds, the transfer stays in &#x60;needs_funds&#x60; and the retry can be repeated.`,
		errors: [
			{
				description: `Outbound transfer not found`,
				schema: z.void(),
				status: 404,
			},
			{
				description: `Outbound transfer is not awaiting funds, or Idempotency-Key concurrency conflict`,
				schema: z.void(),
				status: 409,
			},
			{
				description: `Idempotency-Key reuse for a different request`,
				schema: z.void(),
				status: 422,
			},
		],
		method: "post",
		parameters: [
			{
				name: "operation_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "Idempotency-Key",
				schema: z.string().optional(), // Overridden as optional. Added via a plugin()
				type: "Header",
			},
		],
		path: "/internal/v1/transfers/:operation_id/retry",
		requestFormat: "json",
		response: z.object({ operation_id: z.string() }),
	},
	{
		alias: "retoolLookupUserOrgsByEmail",
		method: "get",
		parameters: [
			{
				name: "email",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/users/:email/organisations",
		requestFormat: "json",
		response: z.array(Organisation),
	},
	{
		alias: "internalRegisterUser",
		method: "post",
		parameters: [
			{
				name: "body",
				schema: RegisterUserRequest,
				type: "Body",
			},
		],
		path: "/internal/v1/users/register",
		requestFormat: "json",
		response: z.object({ org_id: z.string() }),
	},
	{
		alias: "getInternalV2AccountsAllOrganisationsPending",
		method: "get",
		parameters: [
			{
				name: "cursor",
				schema: z.string().optional(),
				type: "Query",
			},
			{
				name: "page_size",
				schema: z.number().int().gte(0).optional(),
				type: "Query",
			},
			{
				name: "organisation_id",
				schema: z.string().optional(),
				type: "Query",
			},
			{
				name: "currency",
				schema: z
					.enum(["AED", "USD", "JOD", "EGP", "SAR", "GBP", "EUR", "HKD", "CNH", "SGD"])
					.optional(),
				type: "Query",
			},
		],
		path: "/internal/v2/accounts/pending",
		requestFormat: "json",
		response: ListPendingAccountsPagedResponse,
	},
	{
		alias: "createCustomerAccountV2",
		errors: [
			{
				description: `Unable to complete request with data provided`,
				schema: z.void(),
				status: 400,
			},
			{
				description: `Customer not found`,
				schema: z.void(),
				status: 404,
			},
			{
				description: `Idempotency-Key concurrency conflict`,
				schema: z.void(),
				status: 409,
			},
			{
				description: `Idempotency-Key reuse for a different request`,
				schema: z.void(),
				status: 422,
			},
		],
		method: "post",
		parameters: [
			{
				name: "body",
				schema: CreateAccountRequestV2,
				type: "Body",
			},
			{
				name: "Idempotency-Key",
				schema: z.string().optional(), // Overridden as optional. Added via a plugin()
				type: "Header",
			},
			{
				name: "customer_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v2/customers/:customer_id/accounts",
		requestFormat: "json",
		response: z.object({ account_id: z.string() }),
	},
	{
		alias: "getInternalV2OrganisationsAccountsPending",
		method: "get",
		parameters: [
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v2/organisations/:organisation_id/accounts/pending",
		requestFormat: "json",
		response: z.array(PendingAccount),
	},
	{
		alias: "getOrganisationFeesV2",
		errors: [
			{
				description: `There is no fee override for the organisation`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "get",
		parameters: [
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v2/organisations/:organisation_id/fees",
		requestFormat: "json",
		response: ReadOrgFeeResponseV2,
	},
	{
		alias: "getAllOrganisationsFeesV2",
		errors: [
			{
				description: `Internal server error`,
				schema: z.void(),
				status: 500,
			},
		],
		method: "get",
		path: "/internal/v2/organisations/all/fees",
		requestFormat: "json",
		response: AllOrganisationsFeesResponseV2,
	},
	{
		alias: "portalGetOrganisationFeesV2",
		errors: [
			{
				description: `There is no fee override for the organisation`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "get",
		path: "/internal/v2/organisations/fees",
		requestFormat: "json",
		response: ReadOrgFeeResponseV2,
	},
	{
		alias: "internalInviteOrganisationUserV2",
		method: "post",
		parameters: [
			{
				name: "body",
				schema: InviteUserRequest,
				type: "Body",
			},
		],
		path: "/internal/v2/organisations/users",
		requestFormat: "json",
		response: z.void(),
	},
	{
		alias: "getOrganisationFeesV3",
		errors: [
			{
				description: `There is no fee override for the organisation`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "get",
		parameters: [
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v3/organisations/:organisation_id/fees",
		requestFormat: "json",
		response: ReadOrgFeeResponseV3,
	},
	{
		alias: "getOrganisationFeesV3Portal",
		errors: [
			{
				description: `There is no fee override for the organisation`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "get",
		path: "/internal/v3/organisations/fees",
		requestFormat: "json",
		response: ReadOrgFeeResponseV3,
	},
	{
		alias: "portalPaymentFeeEstimateForBeneficiary",
		description: `Nexus-aware fee estimate for the given beneficiary. When the payment qualifies for the Nexus
waiver the estimate is zero and &#x60;nexus&#x60; is true; otherwise it is the standard fee.`,
		errors: [
			{
				description: `Unable to complete request with data provided`,
				schema: z.void(),
				status: 400,
			},
			{
				description: `Account not found`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "post",
		parameters: [
			{
				name: "body",
				schema: EstimateFeeForBeneficiaryRequest,
				type: "Body",
			},
			{
				name: "account_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/v1/accounts/:account_id/payments/estimate-fee",
		requestFormat: "json",
		response: EstimateFeeForBeneficiaryResponse,
	},
	{
		alias: "searchAccounts",
		method: "get",
		parameters: [
			{
				name: "cursor",
				schema: cursor,
				type: "Query",
			},
			{
				name: "page_size",
				schema: z.number().int().gte(0).optional(),
				type: "Query",
			},
			{
				name: "query",
				schema: z.string().optional(),
				type: "Query",
			},
			{
				name: "status",
				schema: z.string().optional(),
				type: "Query",
			},
			{
				name: "currency",
				schema: z.string().optional(),
				type: "Query",
			},
			{
				name: "account_type",
				schema: z.enum(["primary", "payment", "virtual"]).optional(),
				type: "Query",
			},
		],
		path: "/v1/accounts/search",
		requestFormat: "json",
		response: SearchAccountsPagedResponse,
	},
	{
		alias: "listCashManagementAccount",
		method: "get",
		path: "/v1/cash-management/accounts",
		requestFormat: "json",
		response: z.array(GetCashManagementAccountResponse),
	},
	{
		alias: "getCashManagementAccount",
		errors: [
			{
				description: `Cash Management account not found`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "get",
		parameters: [
			{
				name: "account_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/v1/cash-management/accounts/:account_id",
		requestFormat: "json",
		response: GetCashManagementAccountResponse,
	},
	{
		alias: "getCashManagementCashLimits",
		errors: [
			{
				description: `Cash Management account not found`,
				schema: z.void(),
				status: 404,
			},
			{
				description: `The bank could not be reached, or does not report cash limits`,
				schema: z.void(),
				status: 500,
			},
		],
		method: "get",
		parameters: [
			{
				name: "account_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/v1/cash-management/accounts/:account_id/cash-limits",
		requestFormat: "json",
		response: CashManagementCashLimitsResponse,
	},
	{
		alias: "requestCashInterestChange",
		description: `Records the requested change and asynchronously asks the bank to apply it; the confirmed
state is reflected on &#x60;GET /cash-management/accounts/{account_id}&#x60; once the bank confirms it.`,
		errors: [
			{
				description: `The account&#x27;s bank does not offer cash interest`,
				schema: z.void(),
				status: 400,
			},
			{
				description: `Cash Management account not found`,
				schema: z.void(),
				status: 404,
			},
			{
				description: `Idempotency-Key concurrency conflict`,
				schema: z.void(),
				status: 409,
			},
			{
				description: `Idempotency-Key reuse for a different request; or the enrollment cannot proceed: no APR tier configured for the organisation, a tier the bank does not offer, or an account that is closed, failed, or not yet provisioned at a bank`,
				schema: z.void(),
				status: 422,
			},
		],
		method: "patch",
		parameters: [
			{
				name: "body",
				schema: RequestCashInterestChangeRequest,
				type: "Body",
			},
			{
				name: "account_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "Idempotency-Key",
				schema: z.string().optional(), // Overridden as optional. Added via a plugin()
				type: "Header",
			},
		],
		path: "/v1/cash-management/accounts/:account_id/interest",
		requestFormat: "json",
		response: z.object({}).partial(),
	},
	{
		alias: "createBuyOrder",
		description: `Buy orders are used to invest cash in a Cash Management Account into a Fund.
When a new order is placed, the following events will happen:

1. The invested cash amount is debited from the Cash Management Account.
2. Cash is used to buy shares of the chosen Fund.
3. The newly bought shares are credited to the organisation&#x27;s Position.`,
		errors: [
			{
				description: `Unable to complete request with data provided`,
				schema: ErrorResponse,
				status: 400,
			},
			{
				description: `Cash Management Account not found`,
				schema: ErrorResponse,
				status: 404,
			},
			{
				description: `Idempotency-Key concurrency conflict`,
				schema: z.void(),
				status: 409,
			},
			{
				description: `Idempotency-Key reuse for a different request`,
				schema: z.void(),
				status: 422,
			},
		],
		method: "post",
		parameters: [
			{
				name: "body",
				schema: CreateBuyOrderRequest,
				type: "Body",
			},
			{
				name: "Idempotency-Key",
				schema: z.string().optional(), // Overridden as optional. Added via a plugin()
				type: "Header",
			},
			{
				name: "cash_management_account_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/v1/cash-management/accounts/:cash_management_account_id/buy",
		requestFormat: "json",
		response: z.object({ buy_order_id: z.string() }),
	},
	{
		alias: "getMarketClock",
		description: `The window already accounts for the pre-close cutoff the order endpoints enforce, so a client
can use it to know ahead of time whether an order would be rejected as &#x60;market_closed&#x60;.`,
		errors: [
			{
				description: `Cash Management Account not found`,
				schema: ErrorResponse,
				status: 404,
			},
		],
		method: "get",
		parameters: [
			{
				name: "cash_management_account_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/v1/cash-management/accounts/:cash_management_account_id/market-clock",
		requestFormat: "json",
		response: GetMarketClockResponse,
	},
	{
		alias: "createSellOrder",
		description: `Sell orders are used to sell owned shares of a Fund in exchange for cash to be credited to a Cash Management Account.
When a new order is placed, the following events will happen:

1. The shares to be sold are debited to the organisation&#x27;s Position.
2. Shares are sold in exchange for cash.
3. Cash is credited to the Cash Management Account.

When owning multiple shares of the same fund bought at different prices,
the system will always sell the oldest ones first when computing profits and losses.`,
		errors: [
			{
				description: `Unable to complete request with data provided`,
				schema: ErrorResponse,
				status: 400,
			},
			{
				description: `Cash Management Account not found`,
				schema: ErrorResponse,
				status: 404,
			},
			{
				description: `Idempotency-Key concurrency conflict`,
				schema: z.void(),
				status: 409,
			},
			{
				description: `Idempotency-Key reuse for a different request`,
				schema: z.void(),
				status: 422,
			},
		],
		method: "post",
		parameters: [
			{
				name: "body",
				schema: CreateSellOrderRequest,
				type: "Body",
			},
			{
				name: "Idempotency-Key",
				schema: z.string().optional(), // Overridden as optional. Added via a plugin()
				type: "Header",
			},
			{
				name: "cash_management_account_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/v1/cash-management/accounts/:cash_management_account_id/sell",
		requestFormat: "json",
		response: z.object({ sell_order_id: z.string() }),
	},
	{
		alias: "listCashManagementAssets",
		description: `Only tradable, outstanding assets are listed. Use the single-asset endpoint to read one
that has since matured or stopped trading.

&#x60;query&#x60; narrows the list to assets whose ISIN, short name or description contains the given
text, case-insensitively. &#x60;asset_class&#x60; and &#x60;bond_type&#x60; narrow it to a category. Filters
combine, and &#x60;total_count&#x60; and &#x60;total_pages&#x60; describe the filtered set.`,
		method: "get",
		parameters: [
			{
				name: "cursor",
				schema: cursor,
				type: "Query",
			},
			{
				name: "page_size",
				schema: page_size,
				type: "Query",
			},
			{
				name: "query",
				schema: cursor,
				type: "Query",
			},
			{
				name: "asset_class",
				schema: asset_class,
				type: "Query",
			},
			{
				name: "bond_type",
				schema: bond_type,
				type: "Query",
			},
		],
		path: "/v1/cash-management/assets",
		requestFormat: "json",
		response: ListCashManagementAssetsResponse,
	},
	{
		alias: "getCashManagementAsset",
		errors: [
			{
				description: `Cash Management asset not found`,
				schema: ErrorResponse,
				status: 404,
			},
		],
		method: "get",
		parameters: [
			{
				name: "isin",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/v1/cash-management/assets/:isin",
		requestFormat: "json",
		response: CashManagementAsset,
	},
	{
		alias: "listPositions",
		description: `Positions are sorted by fund name, position id breaking ties. A position whose shares have
all been sold still lists, with a zero share count: positions are never removed.

&#x60;query&#x60; narrows the list to positions whose fund&#x27;s ISIN, name or description contains the
given text, case-insensitively. Unlike the assets endpoint, &#x60;%&#x60; and &#x60;_&#x60; match literally.
&#x60;total_count&#x60; and &#x60;total_pages&#x60; describe the filtered set.`,
		method: "get",
		parameters: [
			{
				name: "cursor",
				schema: cursor,
				type: "Query",
			},
			{
				name: "page_size",
				schema: page_size,
				type: "Query",
			},
			{
				name: "query",
				schema: cursor,
				type: "Query",
			},
			{
				name: "status",
				schema: status,
				type: "Query",
			},
			{
				name: "cash_management_account_id",
				schema: cursor,
				type: "Query",
			},
		],
		path: "/v1/cash-management/funds/positions",
		requestFormat: "json",
		response: ListCashManagementPositionsResponse,
	},
	{
		alias: "getPosition",
		errors: [
			{
				description: `No position with this id exists for this organisation`,
				schema: ErrorResponse,
				status: 404,
			},
		],
		method: "get",
		parameters: [
			{
				name: "position_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/v1/cash-management/funds/positions/:position_id",
		requestFormat: "json",
		response: CashManagementPosition,
	},
	{
		alias: "listCashManagementTransactions",
		method: "get",
		parameters: [
			{
				name: "cursor",
				schema: cursor,
				type: "Query",
			},
			{
				name: "page_size",
				schema: page_size,
				type: "Query",
			},
			{
				name: "cash_management_account_id",
				schema: cursor,
				type: "Query",
			},
			{
				name: "transaction_type",
				schema: transaction_type,
				type: "Query",
			},
			{
				name: "state",
				schema: state,
				type: "Query",
			},
			{
				name: "from_timestamp",
				schema: cursor,
				type: "Query",
			},
			{
				name: "to_timestamp",
				schema: cursor,
				type: "Query",
			},
		],
		path: "/v1/cash-management/transactions",
		requestFormat: "json",
		response: ListCashManagementTransactionsResponse,
	},
	{
		alias: "getCashManagementTransaction",
		errors: [
			{
				description: `Cash management transaction not found`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "get",
		parameters: [
			{
				name: "transaction_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/v1/cash-management/transactions/:transaction_id",
		requestFormat: "json",
		response: CashManagementTransaction,
	},
	{
		alias: "createCashManagementOutboundTransfer",
		errors: [
			{
				description: `Unable to complete request with data provided`,
				schema: z.void(),
				status: 400,
			},
			{
				description: `Cash Management Account not found`,
				schema: z.void(),
				status: 404,
			},
			{
				description: `Idempotency-Key concurrency conflict`,
				schema: z.void(),
				status: 409,
			},
			{
				description: `Idempotency-Key reuse for a different request`,
				schema: z.void(),
				status: 422,
			},
		],
		method: "post",
		parameters: [
			{
				name: "body",
				schema: CreateTransferRequest,
				type: "Body",
			},
			{
				name: "Idempotency-Key",
				schema: z.string().optional(), // Overridden as optional. Added via a plugin()
				type: "Header",
			},
		],
		path: "/v1/cash-management/transfers",
		requestFormat: "json",
		response: z.object({ transaction_id: z.string() }),
	},
	{
		alias: "getCurrencyOnboardingRequiredDocuments",
		method: "get",
		parameters: [
			{
				name: "customer_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "currency",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/internal/v1/customers/:customer_id/onboarding/:currency/required-documents",
		requestFormat: "json",
		response: OnboardingRequiredDocumentsStatus,
	},
	{
		alias: "getCustomerProductOnboarding",
		description: `Get Customer Onboarding Status for a specific product`,
		errors: [
			{
				description: `Unsupported product`,
				schema: ErrorResponse,
				status: 400,
			},
		],
		method: "get",
		parameters: [
			{
				name: "customer_id",
				schema: zodPathParam,
				type: "Path",
			},
			{
				name: "product",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/v1/customers/:customer_id/onboarding/products/:product",
		requestFormat: "json",
		response: ProductOnboarding,
	},
	{
		alias: "addBusinessWithDocs",
		errors: [
			{
				description: `Invalid request`,
				schema: ErrorResponse,
				status: 400,
			},
			{
				description: `Authentication error`,
				schema: ErrorResponse,
				status: 401,
			},
			{
				description: `Conflict error`,
				schema: ErrorResponse,
				status: 409,
			},
			{
				description: `Internal server error`,
				schema: ErrorResponse,
				status: 500,
			},
		],
		method: "post",
		parameters: [
			{
				name: "body",
				schema: AddBusinessWithDocsRequest,
				type: "Body",
			},
		],
		path: "/v1/customers/businesses/with-docs",
		requestFormat: "json",
		response: z.object({ id: z.string() }),
	},
	{
		alias: "dryRunCustomer",
		description: `Portal-only endpoint. Returns 200 with the list of validation errors; an
empty list means the validation passed.

The uniqueness results are advisory, not a guarantee. Nothing on the create
path enforces display name or registration number uniqueness and there is no
unique index behind either, so a duplicate can still be created between this
call and the create call, or by a client that calls create directly. Only id
number (individual) and tax number (business) are rejected at create time.`,
		errors: [
			{
				description: `Invalid request`,
				schema: ErrorResponse,
				status: 400,
			},
			{
				description: `Authentication error`,
				schema: ErrorResponse,
				status: 401,
			},
			{
				description: `Internal server error`,
				schema: ErrorResponse,
				status: 500,
			},
		],
		method: "post",
		parameters: [
			{
				name: "body",
				schema: CustomerDryRunRequest,
				type: "Body",
			},
		],
		path: "/v1/customers/dry-run",
		requestFormat: "json",
		response: CustomerDryRunResponse,
	},
	{
		alias: "addIndividualWithDocs",
		errors: [
			{
				description: `Invalid request`,
				schema: ErrorResponse,
				status: 400,
			},
			{
				description: `Authentication error`,
				schema: ErrorResponse,
				status: 401,
			},
			{
				description: `Conflict error`,
				schema: ErrorResponse,
				status: 409,
			},
			{
				description: `Internal server error`,
				schema: ErrorResponse,
				status: 500,
			},
		],
		method: "post",
		parameters: [
			{
				name: "body",
				schema: AddIndividualWithDocsRequest,
				type: "Body",
			},
		],
		path: "/v1/customers/individuals/with-docs",
		requestFormat: "json",
		response: z.object({ id: z.string() }),
	},
	{
		alias: "searchCustomers",
		method: "get",
		parameters: [
			{
				name: "cursor",
				schema: cursor,
				type: "Query",
			},
			{
				name: "page_size",
				schema: z.number().int().gte(0).optional(),
				type: "Query",
			},
			{
				name: "query",
				schema: z.string().optional(),
				type: "Query",
			},
			{
				name: "customer_type",
				schema: z.string().optional(),
				type: "Query",
			},
			{
				name: "relationship_type",
				schema: z.string().optional(),
				type: "Query",
			},
			{
				name: "parent_customer_id",
				schema: z.string().optional(),
				type: "Query",
			},
		],
		path: "/v1/customers/search",
		requestFormat: "json",
		response: SearchCustomersPagedResponse,
	},
	{
		alias: "createInternalAccount",
		errors: [
			{
				description: `Organisation not found`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "post",
		parameters: [
			{
				name: "body",
				schema: CreateInternalAccountRequest,
				type: "Body",
			},
			{
				name: "organisation_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/v1/organisations/:organisation_id/internal-accounts",
		requestFormat: "json",
		response: z.object({ account_id: z.string() }),
	},
	{
		alias: "portalGetTransaction",
		errors: [
			{
				description: `Transaction not found`,
				schema: z.void(),
				status: 404,
			},
		],
		method: "get",
		parameters: [
			{
				name: "transaction_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/v1/transactions/:transaction_id",
		requestFormat: "json",
		response: PortalTransaction,
	},
	{
		alias: "portalSearchTransactions",
		method: "get",
		parameters: [
			{
				name: "batch_id",
				schema: z.string().optional(),
				type: "Query",
			},
			{
				name: "transaction_state",
				schema: z
					.enum(["Created", "Submitted", "Executed", "Failed", "Settled"])
					.optional(),
				type: "Query",
			},
			{
				name: "payment_type",
				schema: z
					.enum([
						"Local",
						"InternalTransfer",
						"FXTransfer",
						"LocalReturnPayment",
						"Fee",
						"Transfer",
					])
					.optional(),
				type: "Query",
			},
			{
				name: "customer_account_id",
				schema: z.string().optional(),
				type: "Query",
			},
			{
				name: "from",
				schema: z.string().datetime().optional(),
				type: "Query",
			},
			{
				name: "to",
				schema: z.string().datetime().optional(),
				type: "Query",
			},
			{
				name: "reference",
				schema: z.string().optional(),
				type: "Query",
			},
			{
				name: "external_id",
				schema: z.string().optional(),
				type: "Query",
			},
			{
				name: "account_name",
				schema: z.string().optional(),
				type: "Query",
			},
			{
				name: "cursor",
				schema: cursor,
				type: "Query",
			},
			{
				name: "page_size",
				schema: z.number().int().gte(0).optional(),
				type: "Query",
			},
		],
		path: "/v1/transactions/search",
		requestFormat: "json",
		response: SearchPortalTransactionsPagedResponse,
	},
	{
		alias: "createOutboundTransfer",
		errors: [
			{
				description: `Unable to complete request with data provided`,
				schema: z.void(),
				status: 400,
			},
			{
				description: `Account not found`,
				schema: z.void(),
				status: 404,
			},
			{
				description: `Idempotency-Key concurrency conflict`,
				schema: z.void(),
				status: 409,
			},
			{
				description: `Idempotency-Key reuse for a different request`,
				schema: z.void(),
				status: 422,
			},
		],
		method: "post",
		parameters: [
			{
				name: "body",
				schema: CreateTransferRequest,
				type: "Body",
			},
			{
				name: "Idempotency-Key",
				schema: z.string().optional(), // Overridden as optional. Added via a plugin()
				type: "Header",
			},
		],
		path: "/v1/transfers",
		requestFormat: "json",
		response: z.object({ transaction_id: z.string() }),
	},
	{
		alias: "getCustomerAccountV2",
		method: "get",
		parameters: [
			{
				name: "account_id",
				schema: zodPathParam,
				type: "Path",
			},
		],
		path: "/v2/accounts/:account_id",
		requestFormat: "json",
		response: AccountV2,
	},
	{
		alias: "listPendingCustomerAccountsV2",
		method: "get",
		path: "/v2/accounts/pending",
		requestFormat: "json",
		response: ListPendingCustomerAccountsResponse,
	},
]);

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
	return new Zodios(baseUrl, endpoints, options);
}
